import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ctaCteApiService from '../services/CtaCteApiService';
import DataGrid from './common/DataGrid';
import LoadingSpinner from './common/LoadingSpinner';
import SearchableSelect from './common/SearchableSelect';
import SuscripcionModal from './common/SuscripcionModal';
import UserSessionMenu from './common/UserSessionMenu';
import ctaCteLogo from '../assets/ctacte/logoCtaCte.png';
import './CtaCteDashboard.css';

const menuItems = [
  { id: 'resumen', label: 'Resumen', icon: 'fas fa-chart-line' },
  { id: 'etiquetas', label: 'Etiquetas', icon: 'fas fa-tags' },
  { id: 'cuentas', label: 'Cuentas', icon: 'fas fa-wallet' },
  { id: 'ctacte', label: 'Cta Cte', icon: 'fas fa-file-invoice-dollar' },
  { id: 'comprobantes', label: 'Comprobantes', icon: 'fas fa-file-invoice' },
  { id: 'clientes', label: 'Clientes', icon: 'fas fa-users' },
  { id: 'proveedores', label: 'Proveedores', icon: 'fas fa-truck' },
  { id: 'reportes', label: 'Reportes', icon: 'fas fa-chart-pie' },
  { id: 'procesos', label: 'Procesos', icon: 'fas fa-gears' }
];

const emptyEtiquetaForm = { id: '', nombre: '', descripcion: '', color: '#2563eb' };
const emptyCuentaForm = { id: '', nombre: '', codigo: '', descripcion: '', idEtiqueta: '' };
const emptySubcuentaForm = { id: '', idCuenta: '', nombre: '', codigo: '', descripcion: '' };
const emptyTipoComprobanteForm = { id: '', nombre: '', codigo: '', descripcion: '' };
const emptyEntidadComercialForm = { id: '', nombre: '', documento: '', email: '', telefono: '', direccion: '', notas: '' };
const todayInput = () => new Date().toISOString().slice(0, 10);
const monthStartInput = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};
const emptyMovimientoForm = () => ({
  id: '',
  tipoMovimiento: 'Debito',
  idCuenta: '',
  idSubcuenta: '',
  entidadTipo: '',
  idCliente: '',
  idProveedor: '',
  idComprobante: '',
  concepto: '',
  importe: '',
  fechaMovimiento: todayInput(),
  notas: ''
});
const emptyComprobanteForm = () => ({
  id: '',
  idTipoComprobante: '',
  numero: '',
  fechaComprobante: todayInput(),
  entidadTipo: 'Cliente',
  idCliente: '',
  idProveedor: '',
  importeTotal: '',
  notas: ''
});
const emptyReporteForm = () => ({
  nombre: '',
  fechaDesde: monthStartInput(),
  fechaHasta: todayInput(),
  entidadTipo: '',
  idCliente: '',
  idProveedor: '',
  idTipoComprobante: '',
  tipoMovimiento: '',
  incluirComprobantes: true,
  incluirMovimientos: true
});

const getValue = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
  }
  return undefined;
};

const getList = (item, lowerKey, upperKey) => {
  const value = item?.[lowerKey] || item?.[upperKey] || [];
  return Array.isArray(value) ? value : [];
};

const getId = (item) => String(getValue(item, 'id', 'Id') || '');
const getNombre = (item) => getValue(item, 'nombre', 'Nombre') || '';
const formatCurrency = (value) => new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 2
}).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-AR');
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

const getProcesoEstadoClass = (estado) => {
  if (estado === 'Completado') return 'success';
  if (estado === 'Error') return 'error';
  if (estado === 'EnProceso') return 'processing';
  return 'pending';
};

const parseAmount = (value) => {
  const normalized = String(value || '')
    .replace(/\s/g, '')
    .replace(/\$/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmountInput = (value) => {
  if (String(value || '').trim() === '') return '';
  const amount = parseAmount(value);
  return `$${new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)}`;
};

const sanitizeAmountInput = (value) => String(value || '').replace(/[^\d.,$\s-]/g, '');

const getAmountParts = (value) => {
  const sanitized = sanitizeAmountInput(value).replace(/\./g, '');
  const commaIndex = sanitized.indexOf(',');
  const integerPart = commaIndex >= 0 ? sanitized.slice(0, commaIndex) : sanitized;
  const decimalPart = commaIndex >= 0 ? sanitized.slice(commaIndex + 1) : '';

  return {
    integerDigits: integerPart.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, ''),
    decimalDigits: decimalPart.replace(/[^\d]/g, '').slice(0, 2),
    hasDecimalSeparator: commaIndex >= 0
  };
};

const formatAmountFromParts = ({ integerDigits, decimalDigits = '', decimalMode = false }) => {
  const normalizedDigits = String(integerDigits || '').replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '');
  if (!normalizedDigits && !decimalMode) return '';
  const integerValue = Number(normalizedDigits || 0);
  const integerText = new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 0
  }).format(integerValue);

  if (decimalMode) {
    return `$${integerText},${decimalDigits}`;
  }

  return `$${new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(integerValue)}`;
};

const formatAmountAsTyping = (inputValue, previousValue, inputType, data) => {
  const previousParts = getAmountParts(previousValue);
  const previousDecimalMode = String(previousValue || '').endsWith(',') ||
    (previousParts.hasDecimalSeparator && previousParts.decimalDigits !== '00');

  if (inputType === 'insertText' && /^\d$/.test(data || '')) {
    if (String(inputValue).trim() === data) {
      return formatAmountFromParts({ integerDigits: data });
    }

    if (previousDecimalMode) {
      return formatAmountFromParts({
        integerDigits: previousParts.integerDigits,
        decimalDigits: `${previousParts.decimalDigits}${data}`.slice(0, 2),
        decimalMode: true
      });
    }

    return formatAmountFromParts({ integerDigits: `${previousParts.integerDigits}${data}` });
  }

  if (inputType === 'insertText' && (data === ',' || data === '.')) {
    return formatAmountFromParts({
      integerDigits: previousParts.integerDigits,
      decimalDigits: '',
      decimalMode: true
    });
  }

  if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') {
    if (previousDecimalMode) {
      if (previousParts.decimalDigits.length > 0) {
        return formatAmountFromParts({
          integerDigits: previousParts.integerDigits,
          decimalDigits: previousParts.decimalDigits.slice(0, -1),
          decimalMode: true
        });
      }

      return formatAmountFromParts({ integerDigits: previousParts.integerDigits });
    }

    return formatAmountFromParts({ integerDigits: previousParts.integerDigits.slice(0, -1) });
  }

  return formatAmountInput(sanitizeAmountInput(inputValue));
};

const tipoMovimientoOptions = [
  { value: 'Debito', label: 'Débito' },
  { value: 'Credito', label: 'Crédito' }
];

const tipoMovimientoFilterOptions = [
  { value: '', label: 'Todos los tipos' },
  ...tipoMovimientoOptions
];

const entidadComprobanteOptions = [
  { value: 'Cliente', label: 'Cliente' },
  { value: 'Proveedor', label: 'Proveedor' }
];

const entidadComprobanteFilterOptions = [
  { value: '', label: 'Cliente o proveedor' },
  ...entidadComprobanteOptions
];

const movimientoEntidadOptions = [
  { value: '', label: 'Sin cliente/proveedor' },
  ...entidadComprobanteOptions
];

const movimientoEntidadFilterOptions = [
  { value: '', label: 'Todas las entidades' },
  ...entidadComprobanteOptions
];

const CtaCteDashboard = () => {
  const { user, logout } = useAuth();
  const [activeMenu, setActiveMenu] = useState('resumen');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSuscripcionModal, setShowSuscripcionModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [toast, setToast] = useState({ id: 0, type: '', text: '' });
  const [resumen, setResumen] = useState(null);
  const [etiquetas, setEtiquetas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [tiposComprobante, setTiposComprobante] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [comprobantes, setComprobantes] = useState([]);
  const [procesos, setProcesos] = useState([]);
  const [movimientosResumen, setMovimientosResumen] = useState({ ingresos: 0, salidas: 0, saldo: 0 });
  const [movimientosTotal, setMovimientosTotal] = useState(0);
  const [comprobantesResumen, setComprobantesResumen] = useState({ importeTotal: 0 });
  const [comprobantesTotal, setComprobantesTotal] = useState(0);
  const [procesosTotal, setProcesosTotal] = useState(0);
  const [etiquetaForm, setEtiquetaForm] = useState(emptyEtiquetaForm);
  const [cuentaForm, setCuentaForm] = useState(emptyCuentaForm);
  const [subcuentaForm, setSubcuentaForm] = useState(emptySubcuentaForm);
  const [tipoComprobanteForm, setTipoComprobanteForm] = useState(emptyTipoComprobanteForm);
  const [clienteForm, setClienteForm] = useState(emptyEntidadComercialForm);
  const [proveedorForm, setProveedorForm] = useState(emptyEntidadComercialForm);
  const [comprobanteForm, setComprobanteForm] = useState(emptyComprobanteForm);
  const [reporteForm, setReporteForm] = useState(emptyReporteForm);
  const [showCuentaModal, setShowCuentaModal] = useState(false);
  const [showSubcuentaModal, setShowSubcuentaModal] = useState(false);
  const [showTipoComprobanteModal, setShowTipoComprobanteModal] = useState(false);
  const [showTiposComprobanteManagerModal, setShowTiposComprobanteManagerModal] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  const [showComprobanteModal, setShowComprobanteModal] = useState(false);
  const [comprobanteCtaCtePrompt, setComprobanteCtaCtePrompt] = useState(null);
  const [showMovimientoComprobanteModal, setShowMovimientoComprobanteModal] = useState(false);
  const [selectedSubcuentasCuentaId, setSelectedSubcuentasCuentaId] = useState('');
  const [codeSuggestionLoading, setCodeSuggestionLoading] = useState('');
  const [movimientoForm, setMovimientoForm] = useState(emptyMovimientoForm);
  const [movimientoFilters, setMovimientoFilters] = useState({
    search: '',
    tipoMovimiento: '',
    idCuenta: '',
    idSubcuenta: '',
    entidadTipo: '',
    idCliente: '',
    idProveedor: '',
    idComprobante: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [comprobanteFilters, setComprobanteFilters] = useState({
    search: '',
    idTipoComprobante: '',
    entidadTipo: '',
    idCliente: '',
    idProveedor: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [procesosFilters, setProcesosFilters] = useState({
    search: '',
    estado: '',
    tipoProceso: ''
  });

  const selectedMovimientoCuenta = useMemo(() => (
    cuentas.find((cuenta) => getId(cuenta) === movimientoForm.idCuenta) || null
  ), [cuentas, movimientoForm.idCuenta]);
  const selectedFilterCuenta = useMemo(() => (
    cuentas.find((cuenta) => getId(cuenta) === movimientoFilters.idCuenta) || null
  ), [cuentas, movimientoFilters.idCuenta]);
  const selectedSubcuentasCuenta = useMemo(() => (
    cuentas.find((cuenta) => getId(cuenta) === selectedSubcuentasCuentaId) || null
  ), [cuentas, selectedSubcuentasCuentaId]);
  const movimientoSubcuentas = getList(selectedMovimientoCuenta, 'subcuentas', 'Subcuentas');
  const filterSubcuentas = getList(selectedFilterCuenta, 'subcuentas', 'Subcuentas');
  const selectedSubcuentasRows = getList(selectedSubcuentasCuenta, 'subcuentas', 'Subcuentas');
  const etiquetaOptions = useMemo(() => [
    { value: '', label: 'Sin etiqueta' },
    ...etiquetas
      .filter((etiqueta) => getValue(etiqueta, 'activo', 'Activo') !== false)
      .map((etiqueta) => ({
        value: getId(etiqueta),
        label: getNombre(etiqueta),
        secondary: getValue(etiqueta, 'descripcion', 'Descripcion') || '',
        color: getValue(etiqueta, 'color', 'Color') || ''
      }))
  ], [etiquetas]);
  const cuentaOptions = useMemo(() => (
    cuentas.map((cuenta) => ({
      value: getId(cuenta),
      label: getNombre(cuenta),
      secondary: getValue(cuenta, 'codigo', 'Codigo') || getValue(cuenta, 'descripcion', 'Descripcion') || ''
    }))
  ), [cuentas]);
  const cuentaFilterOptions = useMemo(() => [
    { value: '', label: 'Todas las cuentas' },
    ...cuentaOptions
  ], [cuentaOptions]);
  const movimientoSubcuentaOptions = useMemo(() => [
    { value: '', label: 'Sin subcuenta' },
    ...movimientoSubcuentas.map((subcuenta) => ({
      value: getId(subcuenta),
      label: getNombre(subcuenta),
      secondary: getValue(subcuenta, 'codigo', 'Codigo') || getValue(subcuenta, 'descripcion', 'Descripcion') || ''
    }))
  ], [movimientoSubcuentas]);
  const filterSubcuentaOptions = useMemo(() => [
    { value: '', label: 'Todas las subcuentas' },
    ...filterSubcuentas.map((subcuenta) => ({
      value: getId(subcuenta),
      label: getNombre(subcuenta),
      secondary: getValue(subcuenta, 'codigo', 'Codigo') || getValue(subcuenta, 'descripcion', 'Descripcion') || ''
    }))
  ], [filterSubcuentas]);
  const tipoComprobanteOptions = useMemo(() => [
    { value: '', label: 'Seleccionar tipo' },
    ...tiposComprobante
      .filter((tipo) => getValue(tipo, 'activo', 'Activo') !== false)
      .map((tipo) => ({
        value: getId(tipo),
        label: getNombre(tipo),
        secondary: getValue(tipo, 'codigo', 'Codigo') || getValue(tipo, 'descripcion', 'Descripcion') || ''
      }))
  ], [tiposComprobante]);
  const tipoComprobanteFilterOptions = useMemo(() => [
    { value: '', label: 'Todos los comprobantes' },
    ...tipoComprobanteOptions.filter((option) => option.value)
  ], [tipoComprobanteOptions]);
  const clienteOptions = useMemo(() => [
    { value: '', label: 'Seleccionar cliente' },
    ...clientes
      .filter((cliente) => getValue(cliente, 'activo', 'Activo') !== false)
      .map((cliente) => ({
        value: getId(cliente),
        label: getNombre(cliente),
        secondary: getValue(cliente, 'documento', 'Documento') || getValue(cliente, 'email', 'Email') || ''
      }))
  ], [clientes]);
  const proveedorOptions = useMemo(() => [
    { value: '', label: 'Seleccionar proveedor' },
    ...proveedores
      .filter((proveedor) => getValue(proveedor, 'activo', 'Activo') !== false)
      .map((proveedor) => ({
        value: getId(proveedor),
        label: getNombre(proveedor),
        secondary: getValue(proveedor, 'documento', 'Documento') || getValue(proveedor, 'email', 'Email') || ''
      }))
  ], [proveedores]);
  const getComprobanteEntidadName = useCallback((comprobante) => {
    const entidadTipo = getValue(comprobante, 'entidadTipo', 'EntidadTipo');
    return entidadTipo === 'Proveedor'
      ? getNombre(getValue(comprobante, 'proveedor', 'Proveedor'))
      : getNombre(getValue(comprobante, 'cliente', 'Cliente'));
  }, []);
  const getMovimientoEntidadName = useCallback((movimiento) => {
    const entidadTipo = getValue(movimiento, 'entidadTipo', 'EntidadTipo');
    if (entidadTipo === 'Proveedor') return getNombre(getValue(movimiento, 'proveedor', 'Proveedor'));
    if (entidadTipo === 'Cliente') return getNombre(getValue(movimiento, 'cliente', 'Cliente'));
    return '';
  }, []);
  const buildComprobanteOption = useCallback((comprobante) => {
    const tipo = getNombre(getValue(comprobante, 'tipoComprobante', 'TipoComprobante'));
    const numero = getValue(comprobante, 'numero', 'Numero') || 'Sin número';
    const entidadTipo = getValue(comprobante, 'entidadTipo', 'EntidadTipo');
    const entidadNombre = getComprobanteEntidadName(comprobante);

    return {
      value: getId(comprobante),
      label: [tipo, numero].filter(Boolean).join(' '),
      secondary: [
        entidadTipo,
        entidadNombre,
        formatCurrency(getValue(comprobante, 'importeTotal', 'ImporteTotal'))
      ].filter(Boolean).join(' · ')
    };
  }, [getComprobanteEntidadName]);
  const suggestMovimientoTipoFromComprobante = useCallback((comprobante) => {
    const tipoNombre = getNombre(getValue(comprobante, 'tipoComprobante', 'TipoComprobante')).toLowerCase();
    if (tipoNombre.includes('crédito') || tipoNombre.includes('credito')) return 'Credito';
    return 'Debito';
  }, []);
  const buildMovimientoFromComprobante = useCallback((comprobante) => {
    const entidadTipo = getValue(comprobante, 'entidadTipo', 'EntidadTipo') || '';
    const tipo = getNombre(getValue(comprobante, 'tipoComprobante', 'TipoComprobante'));
    const numero = getValue(comprobante, 'numero', 'Numero') || '';

    return {
      ...emptyMovimientoForm(),
      tipoMovimiento: suggestMovimientoTipoFromComprobante(comprobante),
      entidadTipo,
      idCliente: entidadTipo === 'Cliente' ? String(getValue(comprobante, 'idCliente', 'IdCliente') || '') : '',
      idProveedor: entidadTipo === 'Proveedor' ? String(getValue(comprobante, 'idProveedor', 'IdProveedor') || '') : '',
      idComprobante: getId(comprobante),
      concepto: [tipo, numero].filter(Boolean).join(' '),
      importe: formatAmountInput(getValue(comprobante, 'importeTotal', 'ImporteTotal') || ''),
      fechaMovimiento: String(getValue(comprobante, 'fechaComprobante', 'FechaComprobante') || '').slice(0, 10) || todayInput(),
      notas: getValue(comprobante, 'notas', 'Notas') || ''
    };
  }, [suggestMovimientoTipoFromComprobante]);
  const comprobanteFilterOptions = useMemo(() => [
    { value: '', label: 'Todos los comprobantes' },
    ...comprobantes
      .filter((comprobante) => getValue(comprobante, 'activo', 'Activo') !== false)
      .map(buildComprobanteOption)
  ], [buildComprobanteOption, comprobantes]);
  const movimientoComprobanteOptions = useMemo(() => [
    { value: '', label: 'Sin comprobante' },
    ...comprobantes
      .filter((comprobante) => {
        if (getValue(comprobante, 'activo', 'Activo') === false) return false;

        const entidadTipo = getValue(comprobante, 'entidadTipo', 'EntidadTipo') || '';
        const idCliente = String(getValue(comprobante, 'idCliente', 'IdCliente') || '');
        const idProveedor = String(getValue(comprobante, 'idProveedor', 'IdProveedor') || '');

        if (movimientoForm.entidadTipo && entidadTipo !== movimientoForm.entidadTipo) return false;
        if (movimientoForm.idCliente && idCliente !== movimientoForm.idCliente) return false;
        if (movimientoForm.idProveedor && idProveedor !== movimientoForm.idProveedor) return false;
        return true;
      })
      .map(buildComprobanteOption)
  ], [
    buildComprobanteOption,
    comprobantes,
    movimientoForm.entidadTipo,
    movimientoForm.idCliente,
    movimientoForm.idProveedor
  ]);
  const reportesProcesos = useMemo(() => (
    procesos.filter((proceso) => getValue(proceso, 'tipoProceso', 'TipoProceso') === 'ReporteCtaCte')
  ), [procesos]);
  const ultimoReporteCompletado = useMemo(() => (
    reportesProcesos.find((proceso) => (
      getValue(proceso, 'estado', 'Estado') === 'Completado' &&
      getValue(proceso, 'resultado', 'Resultado')
    )) || null
  ), [reportesProcesos]);
  const ultimoReporteResultado = useMemo(() => (
    ultimoReporteCompletado ? getValue(ultimoReporteCompletado, 'resultado', 'Resultado') : null
  ), [ultimoReporteCompletado]);
  const procesosEnCurso = useMemo(() => (
    procesos.some((proceso) => ['Pendiente', 'EnProceso'].includes(getValue(proceso, 'estado', 'Estado')))
  ), [procesos]);
  const applyComprobanteToMovimientoForm = (value) => {
    const comprobante = comprobantes.find((item) => getId(item) === value);
    setMovimientoForm((prev) => {
      if (!value || !comprobante) {
        return { ...prev, idComprobante: '' };
      }

      const entidadTipo = getValue(comprobante, 'entidadTipo', 'EntidadTipo') || '';
      const idCliente = String(getValue(comprobante, 'idCliente', 'IdCliente') || '');
      const idProveedor = String(getValue(comprobante, 'idProveedor', 'IdProveedor') || '');

      return {
        ...prev,
        idComprobante: value,
        entidadTipo,
        idCliente: entidadTipo === 'Cliente' ? idCliente : '',
        idProveedor: entidadTipo === 'Proveedor' ? idProveedor : ''
      };
    });
  };
  const etiquetasGridRows = useMemo(() => (
    etiquetas.map((etiqueta) => {
      const etiquetaId = getId(etiqueta);
      const cuentasActivas = cuentas.filter((cuenta) => (
        String(getValue(cuenta, 'idEtiqueta', 'IdEtiqueta') || '') === etiquetaId
      ));

      return {
        ...etiqueta,
        cuentasActivas: cuentasActivas.length,
        cuentasActivasNombres: cuentasActivas.map((cuenta) => getNombre(cuenta)).filter(Boolean)
      };
    })
  ), [etiquetas, cuentas]);
  const etiquetaColumns = useMemo(() => [
    {
      key: 'nombre',
      header: 'Nombre',
      value: (etiqueta) => getNombre(etiqueta),
      render: (etiqueta) => (
        <span className="ctacte-label-cell">
          <span className="ctacte-color-dot" style={{ backgroundColor: getValue(etiqueta, 'color', 'Color') }} />
          <strong>{getNombre(etiqueta)}</strong>
        </span>
      )
    },
    {
      key: 'descripcion',
      header: 'Descripción',
      value: (etiqueta) => getValue(etiqueta, 'descripcion', 'Descripcion') || 'Sin descripción'
    },
    {
      key: 'color',
      header: 'Color',
      value: (etiqueta) => getValue(etiqueta, 'color', 'Color') || '-',
      render: (etiqueta) => (
        <span className="ctacte-color-cell">
          <span className="ctacte-color-dot" style={{ backgroundColor: getValue(etiqueta, 'color', 'Color') }} />
          {getValue(etiqueta, 'color', 'Color') || '-'}
        </span>
      )
    },
    {
      key: 'cuentasActivas',
      header: 'Cuentas',
      value: (etiqueta) => etiqueta.cuentasActivas || 0,
      exportValue: (etiqueta) => etiqueta.cuentasActivas || 0
    },
    {
      key: 'estado',
      header: 'Estado',
      value: (etiqueta) => (getValue(etiqueta, 'activo', 'Activo') === false ? 'Inactiva' : 'Activa'),
      render: (etiqueta) => (
        <span className={`ctacte-pill ${getValue(etiqueta, 'activo', 'Activo') === false ? 'inactive' : 'active'}`}>
          {getValue(etiqueta, 'activo', 'Activo') === false ? 'Inactiva' : 'Activa'}
        </span>
      )
    }
  ], []);
  const movimientoColumns = useMemo(() => [
    {
      key: 'fechaMovimiento',
      header: 'Fecha',
      value: (movimiento) => formatDate(getValue(movimiento, 'fechaMovimiento', 'FechaMovimiento')),
      exportValue: (movimiento) => formatDate(getValue(movimiento, 'fechaMovimiento', 'FechaMovimiento'))
    },
    {
      key: 'cuenta',
      header: 'Cuenta',
      value: (movimiento) => getNombre(getValue(movimiento, 'cuenta', 'Cuenta')) || '-'
    },
    {
      key: 'subcuenta',
      header: 'Subcuenta',
      value: (movimiento) => getNombre(getValue(movimiento, 'subcuenta', 'Subcuenta')) || '-'
    },
    {
      key: 'entidad',
      header: 'Entidad',
      value: (movimiento) => {
        const entidadTipo = getValue(movimiento, 'entidadTipo', 'EntidadTipo') || '';
        const entidadNombre = getMovimientoEntidadName(movimiento);
        return [entidadTipo, entidadNombre].filter(Boolean).join(' - ') || '-';
      },
      searchValue: (movimiento) => {
        const cliente = getValue(movimiento, 'cliente', 'Cliente');
        const proveedor = getValue(movimiento, 'proveedor', 'Proveedor');
        return [
          getValue(movimiento, 'entidadTipo', 'EntidadTipo'),
          getMovimientoEntidadName(movimiento),
          getValue(cliente, 'documento', 'Documento'),
          getValue(proveedor, 'documento', 'Documento')
        ].filter(Boolean).join(' ');
      },
      render: (movimiento) => {
        const entidadTipo = getValue(movimiento, 'entidadTipo', 'EntidadTipo');
        const entidadNombre = getMovimientoEntidadName(movimiento);
        if (!entidadTipo && !entidadNombre) return '-';
        return (
          <span className="ctacte-stacked-cell">
            {entidadTipo && <span className={`ctacte-pill ${entidadTipo === 'Proveedor' ? 'provider' : 'client'}`}>{entidadTipo}</span>}
            {entidadNombre && <strong>{entidadNombre}</strong>}
          </span>
        );
      }
    },
    {
      key: 'comprobante',
      header: 'Comprobante',
      value: (movimiento) => {
        const comprobante = getValue(movimiento, 'comprobante', 'Comprobante');
        if (!comprobante) return '-';
        const tipo = getNombre(getValue(comprobante, 'tipoComprobante', 'TipoComprobante'));
        const numero = getValue(comprobante, 'numero', 'Numero') || '';
        return [tipo, numero].filter(Boolean).join(' ') || '-';
      },
      searchValue: (movimiento) => {
        const comprobante = getValue(movimiento, 'comprobante', 'Comprobante');
        if (!comprobante) return '';
        return [
          getNombre(getValue(comprobante, 'tipoComprobante', 'TipoComprobante')),
          getValue(comprobante, 'numero', 'Numero'),
          getValue(comprobante, 'entidadTipo', 'EntidadTipo')
        ].filter(Boolean).join(' ');
      },
      render: (movimiento) => {
        const comprobante = getValue(movimiento, 'comprobante', 'Comprobante');
        if (!comprobante) return '-';
        const tipo = getNombre(getValue(comprobante, 'tipoComprobante', 'TipoComprobante'));
        const numero = getValue(comprobante, 'numero', 'Numero') || '';
        return (
          <span className="ctacte-stacked-cell compact">
            <strong>{[tipo, numero].filter(Boolean).join(' ') || '-'}</strong>
            <span>{formatCurrency(getValue(comprobante, 'importeTotal', 'ImporteTotal'))}</span>
          </span>
        );
      }
    },
    {
      key: 'concepto',
      header: 'Concepto',
      value: (movimiento) => getValue(movimiento, 'concepto', 'Concepto') || getValue(movimiento, 'notas', 'Notas') || '-'
    },
    {
      key: 'tipoMovimiento',
      header: 'Tipo',
      value: (movimiento) => getValue(movimiento, 'tipoMovimiento', 'TipoMovimiento') || '-',
      render: (movimiento) => {
        const tipo = getValue(movimiento, 'tipoMovimiento', 'TipoMovimiento');
        return <span className={`ctacte-pill ${tipo === 'Credito' ? 'credit' : 'debit'}`}>{tipo}</span>;
      }
    },
    {
      key: 'importe',
      header: 'Importe',
      className: 'ctacte-amount-cell',
      value: (movimiento) => formatCurrency(getValue(movimiento, 'importe', 'Importe')),
      exportValue: (movimiento) => Number(getValue(movimiento, 'importe', 'Importe') || 0)
    }
  ], [getMovimientoEntidadName]);
  const tipoComprobanteColumns = useMemo(() => [
    {
      key: 'nombre',
      header: 'Tipo',
      value: (tipo) => getNombre(tipo),
      render: (tipo) => (
        <span className="ctacte-label-cell">
          <strong>{getNombre(tipo)}</strong>
        </span>
      )
    },
    {
      key: 'codigo',
      header: 'Código',
      value: (tipo) => getValue(tipo, 'codigo', 'Codigo') || '-'
    },
    {
      key: 'descripcion',
      header: 'Descripción',
      value: (tipo) => getValue(tipo, 'descripcion', 'Descripcion') || 'Sin descripción'
    },
    {
      key: 'estado',
      header: 'Estado',
      value: (tipo) => (getValue(tipo, 'activo', 'Activo') === false ? 'Inactivo' : 'Activo'),
      render: (tipo) => (
        <span className={`ctacte-pill ${getValue(tipo, 'activo', 'Activo') === false ? 'inactive' : 'active'}`}>
          {getValue(tipo, 'activo', 'Activo') === false ? 'Inactivo' : 'Activo'}
        </span>
      )
    }
  ], []);
  const entidadComercialColumns = useMemo(() => [
    {
      key: 'nombre',
      header: 'Nombre',
      value: (entidad) => getNombre(entidad),
      render: (entidad) => (
        <span className="ctacte-label-cell">
          <strong>{getNombre(entidad)}</strong>
        </span>
      )
    },
    {
      key: 'documento',
      header: 'CUIT/DNI',
      value: (entidad) => getValue(entidad, 'documento', 'Documento') || '-'
    },
    {
      key: 'email',
      header: 'Email',
      value: (entidad) => getValue(entidad, 'email', 'Email') || '-'
    },
    {
      key: 'telefono',
      header: 'Teléfono',
      value: (entidad) => getValue(entidad, 'telefono', 'Telefono') || '-'
    },
    {
      key: 'direccion',
      header: 'Dirección',
      value: (entidad) => getValue(entidad, 'direccion', 'Direccion') || '-'
    },
    {
      key: 'estado',
      header: 'Estado',
      value: (entidad) => (getValue(entidad, 'activo', 'Activo') === false ? 'Inactivo' : 'Activo'),
      render: (entidad) => (
        <span className={`ctacte-pill ${getValue(entidad, 'activo', 'Activo') === false ? 'inactive' : 'active'}`}>
          {getValue(entidad, 'activo', 'Activo') === false ? 'Inactivo' : 'Activo'}
        </span>
      )
    }
  ], []);
  const comprobanteColumns = useMemo(() => [
    {
      key: 'fechaComprobante',
      header: 'Fecha',
      value: (comprobante) => formatDate(getValue(comprobante, 'fechaComprobante', 'FechaComprobante')),
      exportValue: (comprobante) => formatDate(getValue(comprobante, 'fechaComprobante', 'FechaComprobante'))
    },
    {
      key: 'tipoComprobante',
      header: 'Tipo',
      value: (comprobante) => getNombre(getValue(comprobante, 'tipoComprobante', 'TipoComprobante')) || '-'
    },
    {
      key: 'numero',
      header: 'Número',
      value: (comprobante) => getValue(comprobante, 'numero', 'Numero') || '-'
    },
    {
      key: 'entidadTipo',
      header: 'Entidad',
      value: (comprobante) => getValue(comprobante, 'entidadTipo', 'EntidadTipo') || '-',
      render: (comprobante) => {
        const entidadTipo = getValue(comprobante, 'entidadTipo', 'EntidadTipo');
        return <span className={`ctacte-pill ${entidadTipo === 'Proveedor' ? 'provider' : 'client'}`}>{entidadTipo || '-'}</span>;
      }
    },
    {
      key: 'clienteProveedor',
      header: 'Cliente / Proveedor',
      value: (comprobante) => getComprobanteEntidadName(comprobante) || '-',
      searchValue: (comprobante) => {
        const cliente = getValue(comprobante, 'cliente', 'Cliente');
        const proveedor = getValue(comprobante, 'proveedor', 'Proveedor');
        return [
          getComprobanteEntidadName(comprobante),
          getValue(cliente, 'documento', 'Documento'),
          getValue(proveedor, 'documento', 'Documento')
        ].filter(Boolean).join(' ');
      }
    },
    {
      key: 'importeTotal',
      header: 'Importe total',
      className: 'ctacte-amount-cell',
      value: (comprobante) => formatCurrency(getValue(comprobante, 'importeTotal', 'ImporteTotal')),
      exportValue: (comprobante) => Number(getValue(comprobante, 'importeTotal', 'ImporteTotal') || 0)
    },
    {
      key: 'notas',
      header: 'Notas',
      value: (comprobante) => getValue(comprobante, 'notas', 'Notas') || '-'
    }
  ], [getComprobanteEntidadName]);

  useEffect(() => {
    if (!toast.text) return undefined;

    const timer = window.setTimeout(() => {
      setToast((currentToast) => (
        currentToast.id === toast.id ? { id: 0, type: '', text: '' } : currentToast
      ));
    }, 4200);

    return () => window.clearTimeout(timer);
  }, [toast.id, toast.text]);

  const showToast = useCallback((type, text) => {
    setToast({ id: Date.now(), type, text });
  }, []);

  const showError = useCallback((error, fallback) => {
    showToast('error', error?.message || fallback);
  }, [showToast]);

  const showSuccess = useCallback((text) => {
    showToast('success', text);
  }, [showToast]);

  const closeToast = () => {
    setToast({ id: 0, type: '', text: '' });
  };

  const runWithProcessing = async (message, action) => {
    setSaving(true);
    setProcessingMessage(message);
    try {
      return await action();
    } finally {
      setSaving(false);
      setProcessingMessage('');
    }
  };

  const loadSuggestedCuentaCode = async () => {
    setCodeSuggestionLoading('cuenta');
    try {
      const response = await ctaCteApiService.suggestCuentaCode();
      const suggestedCode = getValue(response, 'codigo', 'Codigo');
      if (suggestedCode) {
        setCuentaForm((prev) => (prev.id || prev.codigo ? prev : { ...prev, codigo: suggestedCode }));
      }
    } catch (error) {
      showError(error, 'No pudimos sugerir el código de cuenta.');
    } finally {
      setCodeSuggestionLoading('');
    }
  };

  const loadSuggestedSubcuentaCode = async (cuentaId) => {
    if (!cuentaId) return;

    setCodeSuggestionLoading('subcuenta');
    try {
      const response = await ctaCteApiService.suggestSubcuentaCode(cuentaId);
      const suggestedCode = getValue(response, 'codigo', 'Codigo');
      if (suggestedCode) {
        setSubcuentaForm((prev) => (prev.id || prev.codigo ? prev : { ...prev, codigo: suggestedCode }));
      }
    } catch (error) {
      showError(error, 'No pudimos sugerir el código de subcuenta.');
    } finally {
      setCodeSuggestionLoading('');
    }
  };

  const openCreateCuentaModal = () => {
    setCuentaForm(emptyCuentaForm);
    setShowCuentaModal(true);
    loadSuggestedCuentaCode();
  };

  const closeCuentaModal = () => {
    if (saving) return;
    setShowCuentaModal(false);
    setCuentaForm(emptyCuentaForm);
  };

  const closeSubcuentaModal = () => {
    if (saving) return;
    setShowSubcuentaModal(false);
    setSubcuentaForm(emptySubcuentaForm);
  };

  const openSubcuentasDetail = (cuenta) => {
    setSelectedSubcuentasCuentaId(getId(cuenta));
  };

  const closeSubcuentasDetail = () => {
    setSelectedSubcuentasCuentaId('');
  };

  const loadBaseData = useCallback(async ({ showPageLoading = true } = {}) => {
    if (showPageLoading) setLoading(true);
    try {
      const [
        resumenRes,
        etiquetasRes,
        cuentasRes,
        movimientosRes,
        tiposRes,
        clientesRes,
        proveedoresRes,
        comprobantesRes,
        procesosRes
      ] = await Promise.all([
        ctaCteApiService.getResumen(),
        ctaCteApiService.getEtiquetas({ incluirInactivas: true }),
        ctaCteApiService.getCuentas(),
        ctaCteApiService.getMovimientos({ take: 50 }),
        ctaCteApiService.getTiposComprobante({ incluirInactivas: true }),
        ctaCteApiService.getClientes({ incluirInactivos: true }),
        ctaCteApiService.getProveedores({ incluirInactivos: true }),
        ctaCteApiService.getComprobantes({ take: 100 }),
        ctaCteApiService.getProcesos({ take: 100 })
      ]);
      setResumen(resumenRes);
      setEtiquetas(getList(etiquetasRes, 'etiquetas', 'Etiquetas'));
      setCuentas(getList(cuentasRes, 'cuentas', 'Cuentas'));
      setMovimientos(getList(movimientosRes, 'movimientos', 'Movimientos'));
      setTiposComprobante(getList(tiposRes, 'tipos', 'Tipos'));
      setClientes(getList(clientesRes, 'clientes', 'Clientes'));
      setProveedores(getList(proveedoresRes, 'proveedores', 'Proveedores'));
      setComprobantes(getList(comprobantesRes, 'comprobantes', 'Comprobantes'));
      setProcesos(getList(procesosRes, 'procesos', 'Procesos'));
      setMovimientosResumen(movimientosRes?.resumen || movimientosRes?.Resumen || { ingresos: 0, salidas: 0, saldo: 0 });
      setMovimientosTotal(Number(getValue(movimientosRes, 'total', 'Total')) || 0);
      setComprobantesResumen(comprobantesRes?.resumen || comprobantesRes?.Resumen || { importeTotal: 0 });
      setComprobantesTotal(Number(getValue(comprobantesRes, 'total', 'Total')) || 0);
      setProcesosTotal(Number(getValue(procesosRes, 'total', 'Total')) || 0);
    } catch (error) {
      showError(error, 'No pudimos cargar la cuenta corriente.');
    } finally {
      if (showPageLoading) setLoading(false);
    }
  }, [showError]);

  const loadMovimientos = useCallback(async (filters = movimientoFilters) => {
    try {
      const response = await ctaCteApiService.getMovimientos({ ...filters, take: 100 });
      setMovimientos(getList(response, 'movimientos', 'Movimientos'));
      setMovimientosResumen(response?.resumen || response?.Resumen || { ingresos: 0, salidas: 0, saldo: 0 });
      setMovimientosTotal(Number(getValue(response, 'total', 'Total')) || 0);
    } catch (error) {
      showError(error, 'No pudimos cargar los movimientos.');
    }
  }, [movimientoFilters, showError]);

  const loadComprobantes = useCallback(async (filters = comprobanteFilters) => {
    try {
      const response = await ctaCteApiService.getComprobantes({ ...filters, take: 100 });
      setComprobantes(getList(response, 'comprobantes', 'Comprobantes'));
      setComprobantesResumen(response?.resumen || response?.Resumen || { importeTotal: 0 });
      setComprobantesTotal(Number(getValue(response, 'total', 'Total')) || 0);
    } catch (error) {
      showError(error, 'No pudimos cargar los comprobantes.');
    }
  }, [comprobanteFilters, showError]);

  const loadProcesos = useCallback(async (filters = procesosFilters) => {
    try {
      const response = await ctaCteApiService.getProcesos({ ...filters, take: 100 });
      setProcesos(getList(response, 'procesos', 'Procesos'));
      setProcesosTotal(Number(getValue(response, 'total', 'Total')) || 0);
    } catch (error) {
      showError(error, 'No pudimos cargar los procesos.');
    }
  }, [procesosFilters, showError]);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    if (!['reportes', 'procesos'].includes(activeMenu) || !procesosEnCurso) return undefined;
    const timer = window.setInterval(() => {
      loadProcesos(procesosFilters);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [activeMenu, loadProcesos, procesosEnCurso, procesosFilters]);

  const refreshCatalogs = async ({ includeResumen = true } = {}) => {
    const [etiquetasRes, cuentasRes, resumenRes] = await Promise.all([
      ctaCteApiService.getEtiquetas({ incluirInactivas: true }),
      ctaCteApiService.getCuentas(),
      includeResumen ? ctaCteApiService.getResumen() : Promise.resolve(null)
    ]);
    setEtiquetas(getList(etiquetasRes, 'etiquetas', 'Etiquetas'));
    setCuentas(getList(cuentasRes, 'cuentas', 'Cuentas'));
    if (includeResumen) setResumen(resumenRes);
  };

  const refreshMovimientoResults = async (filters = movimientoFilters) => {
    const [resumenRes, movimientosRes] = await Promise.all([
      ctaCteApiService.getResumen(),
      ctaCteApiService.getMovimientos({ ...filters, take: 100 })
    ]);
    setResumen(resumenRes);
    setMovimientos(getList(movimientosRes, 'movimientos', 'Movimientos'));
    setMovimientosResumen(movimientosRes?.resumen || movimientosRes?.Resumen || { ingresos: 0, salidas: 0, saldo: 0 });
    setMovimientosTotal(Number(getValue(movimientosRes, 'total', 'Total')) || 0);
  };

  const refreshComprobanteCatalogs = async () => {
    const [tiposRes, clientesRes, proveedoresRes] = await Promise.all([
      ctaCteApiService.getTiposComprobante({ incluirInactivas: true }),
      ctaCteApiService.getClientes({ incluirInactivos: true }),
      ctaCteApiService.getProveedores({ incluirInactivos: true })
    ]);
    setTiposComprobante(getList(tiposRes, 'tipos', 'Tipos'));
    setClientes(getList(clientesRes, 'clientes', 'Clientes'));
    setProveedores(getList(proveedoresRes, 'proveedores', 'Proveedores'));
  };

  const refreshComprobanteResults = async (filters = comprobanteFilters) => {
    const [resumenRes, comprobantesRes] = await Promise.all([
      ctaCteApiService.getResumen(),
      ctaCteApiService.getComprobantes({ ...filters, take: 100 })
    ]);
    setResumen(resumenRes);
    setComprobantes(getList(comprobantesRes, 'comprobantes', 'Comprobantes'));
    setComprobantesResumen(comprobantesRes?.resumen || comprobantesRes?.Resumen || { importeTotal: 0 });
    setComprobantesTotal(Number(getValue(comprobantesRes, 'total', 'Total')) || 0);
  };

  const createReporte = async (event) => {
    event.preventDefault();
    try {
      await runWithProcessing('Encolando reporte...', async () => {
        const payload = {
          ...reporteForm,
          idCliente: reporteForm.entidadTipo === 'Cliente' ? (reporteForm.idCliente || null) : null,
          idProveedor: reporteForm.entidadTipo === 'Proveedor' ? (reporteForm.idProveedor || null) : null,
          idTipoComprobante: reporteForm.idTipoComprobante || null,
          entidadTipo: reporteForm.entidadTipo || null,
          tipoMovimiento: reporteForm.tipoMovimiento || null,
          nombre: reporteForm.nombre || null
        };
        await ctaCteApiService.createReporte(payload);
        const nextProcessFilters = { search: '', estado: '', tipoProceso: '' };
        setProcesosFilters(nextProcessFilters);
        await loadProcesos(nextProcessFilters);
      });
      showSuccess('Reporte encolado. Podés seguir el avance en Procesos.');
      setActiveMenu('procesos');
    } catch (error) {
      showError(error, 'No pudimos encolar el reporte.');
    }
  };

  const downloadProceso = async (proceso, formato) => {
    try {
      const fileName = formato === 'excel'
        ? getValue(proceso, 'archivoExcelNombre', 'ArchivoExcelNombre')
        : getValue(proceso, 'archivoPdfNombre', 'ArchivoPdfNombre');
      await runWithProcessing(`Descargando ${formato === 'excel' ? 'Excel' : 'PDF'}...`, () => (
        ctaCteApiService.downloadProceso(getId(proceso), formato, fileName)
      ));
    } catch (error) {
      showError(error, 'No pudimos descargar el reporte.');
    }
  };

  const resetReporteForm = () => {
    setReporteForm(emptyReporteForm());
  };

  const saveEtiqueta = async (event) => {
    event.preventDefault();
    const message = etiquetaForm.id ? 'Guardando etiqueta...' : 'Creando etiqueta...';
    try {
      await runWithProcessing(message, async () => {
        const payload = {
          nombre: etiquetaForm.nombre,
          descripcion: etiquetaForm.descripcion,
          color: etiquetaForm.color
        };
        if (etiquetaForm.id) {
          await ctaCteApiService.updateEtiqueta(etiquetaForm.id, payload);
        } else {
          await ctaCteApiService.createEtiqueta(payload);
        }
        setEtiquetaForm(emptyEtiquetaForm);
        await refreshCatalogs({ includeResumen: false });
      });
      showSuccess('Etiqueta guardada correctamente.');
    } catch (error) {
      showError(error, 'No pudimos guardar la etiqueta.');
    }
  };

  const editEtiqueta = (etiqueta) => {
    setEtiquetaForm({
      id: getId(etiqueta),
      nombre: getNombre(etiqueta),
      descripcion: getValue(etiqueta, 'descripcion', 'Descripcion') || '',
      color: getValue(etiqueta, 'color', 'Color') || '#2563eb'
    });
  };

  const getEtiquetaUsage = (etiqueta) => {
    const etiquetaId = getId(etiqueta);
    const cuentasAsociadas = cuentas.filter((cuenta) => (
      String(getValue(cuenta, 'idEtiqueta', 'IdEtiqueta') || '') === etiquetaId
    ));

    return {
      count: cuentasAsociadas.length,
      names: cuentasAsociadas.map((cuenta) => getNombre(cuenta)).filter(Boolean)
    };
  };

  const formatUsageNames = (usage) => {
    if (usage.count === 0) return 'No tiene cuentas asociadas.';

    const visibleNames = usage.names.slice(0, 5).join(', ');
    const remaining = usage.count > 5 ? ` y ${usage.count - 5} más` : '';
    return `Cuentas asociadas (${usage.count}): ${visibleNames}${remaining}.`;
  };

  const deactivateEtiqueta = async (etiqueta) => {
    const usage = getEtiquetaUsage(etiqueta);
    const message = [
      `¿Desactivar la etiqueta "${getNombre(etiqueta)}"?`,
      formatUsageNames(usage),
      'La etiqueta dejará de aparecer en el listado y no podrá seleccionarse para nuevas cuentas.',
      'No se eliminan cuentas, subcuentas ni movimientos.'
    ].join('\n\n');

    if (!window.confirm(message)) return;

    try {
      await runWithProcessing('Desactivando etiqueta...', async () => {
        await ctaCteApiService.deleteEtiqueta(getId(etiqueta));
        await refreshCatalogs({ includeResumen: false });
      });
      showSuccess('Etiqueta desactivada.');
    } catch (error) {
      showError(error, 'No pudimos desactivar la etiqueta.');
    }
  };

  const deleteEtiqueta = async (etiqueta) => {
    const usage = getEtiquetaUsage(etiqueta);
    const message = [
      `¿Eliminar permanentemente la etiqueta "${getNombre(etiqueta)}"?`,
      formatUsageNames(usage),
      usage.count > 0
        ? 'Las cuentas asociadas quedarán sin etiqueta.'
        : 'No hay cuentas que deban desvincularse.',
      'No se eliminan cuentas, subcuentas ni movimientos.',
      'Esta acción no se puede revertir desde la app.'
    ].join('\n\n');

    if (!window.confirm(message)) return;

    try {
      await runWithProcessing('Eliminando etiqueta...', async () => {
        await ctaCteApiService.deleteEtiquetaPermanently(getId(etiqueta));
        await refreshCatalogs({ includeResumen: false });
      });
      showSuccess('Etiqueta eliminada permanentemente.');
    } catch (error) {
      showError(error, 'No pudimos eliminar permanentemente la etiqueta.');
    }
  };

  const saveCuenta = async (event) => {
    event.preventDefault();
    const message = cuentaForm.id ? 'Guardando cuenta...' : 'Creando cuenta...';
    try {
      await runWithProcessing(message, async () => {
        const payload = {
          nombre: cuentaForm.nombre,
          codigo: cuentaForm.codigo,
          descripcion: cuentaForm.descripcion,
          idEtiqueta: cuentaForm.idEtiqueta || null
        };
        if (cuentaForm.id) {
          await ctaCteApiService.updateCuenta(cuentaForm.id, payload);
          await loadMovimientos();
        } else {
          await ctaCteApiService.createCuenta(payload);
        }
        setCuentaForm(emptyCuentaForm);
        setShowCuentaModal(false);
        await refreshCatalogs();
      });
      showSuccess('Cuenta guardada correctamente.');
    } catch (error) {
      showError(error, 'No pudimos guardar la cuenta.');
    }
  };

  const editCuenta = (cuenta) => {
    setCuentaForm({
      id: getId(cuenta),
      nombre: getNombre(cuenta),
      codigo: getValue(cuenta, 'codigo', 'Codigo') || '',
      descripcion: getValue(cuenta, 'descripcion', 'Descripcion') || '',
      idEtiqueta: getValue(cuenta, 'idEtiqueta', 'IdEtiqueta') || ''
    });
    setShowCuentaModal(true);
  };

  const deleteCuenta = async (cuenta) => {
    const subcuentas = getList(cuenta, 'subcuentas', 'Subcuentas');
    const subcuentasText = subcuentas.length
      ? `También se desactivarán sus subcuentas (${subcuentas.length}): ${subcuentas.map((subcuenta) => getNombre(subcuenta)).slice(0, 5).join(', ')}${subcuentas.length > 5 ? ` y ${subcuentas.length - 5} más` : ''}.`
      : 'La cuenta no tiene subcuentas activas.';
    const message = [
      `¿Eliminar la cuenta "${getNombre(cuenta)}"?`,
      subcuentasText,
      'No se eliminan los movimientos ya cargados.'
    ].join('\n\n');

    if (!window.confirm(message)) return;
    try {
      await runWithProcessing('Eliminando cuenta...', async () => {
        await ctaCteApiService.deleteCuenta(getId(cuenta));
        await Promise.all([
          refreshCatalogs(),
          loadMovimientos()
        ]);
      });
      showSuccess('Cuenta eliminada.');
    } catch (error) {
      showError(error, 'No pudimos eliminar la cuenta.');
    }
  };

  const startSubcuenta = (cuenta, subcuenta = null) => {
    const cuentaId = getId(cuenta);
    setSelectedSubcuentasCuentaId('');
    setSubcuentaForm({
      id: subcuenta ? getId(subcuenta) : '',
      idCuenta: cuentaId,
      nombre: subcuenta ? getNombre(subcuenta) : '',
      codigo: subcuenta ? (getValue(subcuenta, 'codigo', 'Codigo') || '') : '',
      descripcion: subcuenta ? (getValue(subcuenta, 'descripcion', 'Descripcion') || '') : ''
    });
    setShowSubcuentaModal(true);

    if (!subcuenta) {
      loadSuggestedSubcuentaCode(cuentaId);
    }
  };

  const saveSubcuenta = async (event) => {
    event.preventDefault();
    if (!subcuentaForm.idCuenta) return;
    const message = subcuentaForm.id ? 'Guardando subcuenta...' : 'Creando subcuenta...';
    try {
      await runWithProcessing(message, async () => {
        const payload = {
          nombre: subcuentaForm.nombre,
          codigo: subcuentaForm.codigo,
          descripcion: subcuentaForm.descripcion
        };
        if (subcuentaForm.id) {
          await ctaCteApiService.updateSubcuenta(subcuentaForm.idCuenta, subcuentaForm.id, payload);
          await loadMovimientos();
        } else {
          await ctaCteApiService.createSubcuenta(subcuentaForm.idCuenta, payload);
        }
        setSubcuentaForm(emptySubcuentaForm);
        setShowSubcuentaModal(false);
        await refreshCatalogs();
      });
      showSuccess('Subcuenta guardada correctamente.');
    } catch (error) {
      showError(error, 'No pudimos guardar la subcuenta.');
    }
  };

  const deleteSubcuenta = async (cuenta, subcuenta) => {
    if (!window.confirm('¿Eliminar esta subcuenta?')) return;
    try {
      await runWithProcessing('Eliminando subcuenta...', async () => {
        await ctaCteApiService.deleteSubcuenta(getId(cuenta), getId(subcuenta));
        await Promise.all([
          refreshCatalogs(),
          loadMovimientos()
        ]);
      });
      showSuccess('Subcuenta eliminada.');
    } catch (error) {
      showError(error, 'No pudimos eliminar la subcuenta.');
    }
  };

  const openTiposComprobanteManagerModal = () => {
    setShowTiposComprobanteManagerModal(true);
  };

  const closeTiposComprobanteManagerModal = () => {
    if (saving) return;
    setShowTiposComprobanteManagerModal(false);
  };

  const openCreateTipoComprobanteModal = () => {
    setTipoComprobanteForm(emptyTipoComprobanteForm);
    setShowTipoComprobanteModal(true);
  };

  const closeTipoComprobanteModal = () => {
    if (saving) return;
    setTipoComprobanteForm(emptyTipoComprobanteForm);
    setShowTipoComprobanteModal(false);
  };

  const saveTipoComprobante = async (event) => {
    event.preventDefault();
    const message = tipoComprobanteForm.id ? 'Guardando tipo de comprobante...' : 'Creando tipo de comprobante...';
    try {
      await runWithProcessing(message, async () => {
        const payload = {
          nombre: tipoComprobanteForm.nombre,
          codigo: tipoComprobanteForm.codigo,
          descripcion: tipoComprobanteForm.descripcion
        };
        if (tipoComprobanteForm.id) {
          await ctaCteApiService.updateTipoComprobante(tipoComprobanteForm.id, payload);
        } else {
          await ctaCteApiService.createTipoComprobante(payload);
        }
        setTipoComprobanteForm(emptyTipoComprobanteForm);
        setShowTipoComprobanteModal(false);
        await refreshComprobanteCatalogs();
      });
      showSuccess('Tipo de comprobante guardado correctamente.');
    } catch (error) {
      showError(error, 'No pudimos guardar el tipo de comprobante.');
    }
  };

  const editTipoComprobante = (tipo) => {
    setTipoComprobanteForm({
      id: getId(tipo),
      nombre: getNombre(tipo),
      codigo: getValue(tipo, 'codigo', 'Codigo') || '',
      descripcion: getValue(tipo, 'descripcion', 'Descripcion') || ''
    });
    setShowTipoComprobanteModal(true);
  };

  const deleteTipoComprobante = async (tipo) => {
    const message = [
      `¿Desactivar el tipo "${getNombre(tipo)}"?`,
      'Dejará de aparecer para nuevos comprobantes.',
      'Los comprobantes ya cargados no se eliminan.'
    ].join('\n\n');
    if (!window.confirm(message)) return;

    try {
      await runWithProcessing('Desactivando tipo de comprobante...', async () => {
        await ctaCteApiService.deleteTipoComprobante(getId(tipo));
        await refreshComprobanteCatalogs();
      });
      showSuccess('Tipo de comprobante desactivado.');
    } catch (error) {
      showError(error, 'No pudimos desactivar el tipo de comprobante.');
    }
  };

  const openCreateClienteModal = () => {
    setClienteForm(emptyEntidadComercialForm);
    setShowClienteModal(true);
  };

  const closeClienteModal = () => {
    if (saving) return;
    setClienteForm(emptyEntidadComercialForm);
    setShowClienteModal(false);
  };

  const saveCliente = async (event) => {
    event.preventDefault();
    const message = clienteForm.id ? 'Guardando cliente...' : 'Creando cliente...';
    try {
      await runWithProcessing(message, async () => {
        const payload = {
          nombre: clienteForm.nombre,
          documento: clienteForm.documento,
          email: clienteForm.email,
          telefono: clienteForm.telefono,
          direccion: clienteForm.direccion,
          notas: clienteForm.notas
        };
        if (clienteForm.id) {
          await ctaCteApiService.updateCliente(clienteForm.id, payload);
          await loadComprobantes();
        } else {
          await ctaCteApiService.createCliente(payload);
        }
        setClienteForm(emptyEntidadComercialForm);
        setShowClienteModal(false);
        await refreshComprobanteCatalogs();
      });
      showSuccess('Cliente guardado correctamente.');
    } catch (error) {
      showError(error, 'No pudimos guardar el cliente.');
    }
  };

  const editCliente = (cliente) => {
    setClienteForm({
      id: getId(cliente),
      nombre: getNombre(cliente),
      documento: getValue(cliente, 'documento', 'Documento') || '',
      email: getValue(cliente, 'email', 'Email') || '',
      telefono: getValue(cliente, 'telefono', 'Telefono') || '',
      direccion: getValue(cliente, 'direccion', 'Direccion') || '',
      notas: getValue(cliente, 'notas', 'Notas') || ''
    });
    setShowClienteModal(true);
  };

  const deleteCliente = async (cliente) => {
    if (!window.confirm(`¿Desactivar el cliente "${getNombre(cliente)}"?\n\nLos comprobantes ya cargados no se eliminan.`)) return;
    try {
      await runWithProcessing('Desactivando cliente...', async () => {
        await ctaCteApiService.deleteCliente(getId(cliente));
        await Promise.all([
          refreshComprobanteCatalogs(),
          loadComprobantes()
        ]);
      });
      showSuccess('Cliente desactivado.');
    } catch (error) {
      showError(error, 'No pudimos desactivar el cliente.');
    }
  };

  const openCreateProveedorModal = () => {
    setProveedorForm(emptyEntidadComercialForm);
    setShowProveedorModal(true);
  };

  const closeProveedorModal = () => {
    if (saving) return;
    setProveedorForm(emptyEntidadComercialForm);
    setShowProveedorModal(false);
  };

  const saveProveedor = async (event) => {
    event.preventDefault();
    const message = proveedorForm.id ? 'Guardando proveedor...' : 'Creando proveedor...';
    try {
      await runWithProcessing(message, async () => {
        const payload = {
          nombre: proveedorForm.nombre,
          documento: proveedorForm.documento,
          email: proveedorForm.email,
          telefono: proveedorForm.telefono,
          direccion: proveedorForm.direccion,
          notas: proveedorForm.notas
        };
        if (proveedorForm.id) {
          await ctaCteApiService.updateProveedor(proveedorForm.id, payload);
          await loadComprobantes();
        } else {
          await ctaCteApiService.createProveedor(payload);
        }
        setProveedorForm(emptyEntidadComercialForm);
        setShowProveedorModal(false);
        await refreshComprobanteCatalogs();
      });
      showSuccess('Proveedor guardado correctamente.');
    } catch (error) {
      showError(error, 'No pudimos guardar el proveedor.');
    }
  };

  const editProveedor = (proveedor) => {
    setProveedorForm({
      id: getId(proveedor),
      nombre: getNombre(proveedor),
      documento: getValue(proveedor, 'documento', 'Documento') || '',
      email: getValue(proveedor, 'email', 'Email') || '',
      telefono: getValue(proveedor, 'telefono', 'Telefono') || '',
      direccion: getValue(proveedor, 'direccion', 'Direccion') || '',
      notas: getValue(proveedor, 'notas', 'Notas') || ''
    });
    setShowProveedorModal(true);
  };

  const deleteProveedor = async (proveedor) => {
    if (!window.confirm(`¿Desactivar el proveedor "${getNombre(proveedor)}"?\n\nLos comprobantes ya cargados no se eliminan.`)) return;
    try {
      await runWithProcessing('Desactivando proveedor...', async () => {
        await ctaCteApiService.deleteProveedor(getId(proveedor));
        await Promise.all([
          refreshComprobanteCatalogs(),
          loadComprobantes()
        ]);
      });
      showSuccess('Proveedor desactivado.');
    } catch (error) {
      showError(error, 'No pudimos desactivar el proveedor.');
    }
  };

  const openCreateComprobanteModal = () => {
    setComprobanteForm(emptyComprobanteForm());
    setShowComprobanteModal(true);
  };

  const closeComprobanteModal = () => {
    if (saving) return;
    setComprobanteForm(emptyComprobanteForm());
    setShowComprobanteModal(false);
  };

  const closeComprobanteCtaCtePrompt = () => {
    if (saving) return;
    setComprobanteCtaCtePrompt(null);
    showSuccess('Comprobante guardado sin movimiento de cuenta corriente.');
  };

  const openMovimientoFromComprobante = () => {
    if (!comprobanteCtaCtePrompt) return;
    setMovimientoForm(buildMovimientoFromComprobante(comprobanteCtaCtePrompt));
    setComprobanteCtaCtePrompt(null);
    setShowMovimientoComprobanteModal(true);
  };

  const closeMovimientoComprobanteModal = () => {
    if (saving) return;
    setShowMovimientoComprobanteModal(false);
    setMovimientoForm(emptyMovimientoForm());
  };

  const saveComprobante = async (event) => {
    event.preventDefault();
    if (!comprobanteForm.idTipoComprobante) {
      showError(null, 'Seleccioná un tipo de comprobante.');
      return;
    }
    if (comprobanteForm.entidadTipo === 'Cliente' && !comprobanteForm.idCliente) {
      showError(null, 'Seleccioná un cliente.');
      return;
    }
    if (comprobanteForm.entidadTipo === 'Proveedor' && !comprobanteForm.idProveedor) {
      showError(null, 'Seleccioná un proveedor.');
      return;
    }

    const isEditing = Boolean(comprobanteForm.id);
    let savedComprobante = null;
    const message = comprobanteForm.id ? 'Guardando comprobante...' : 'Creando comprobante...';
    try {
      await runWithProcessing(message, async () => {
        const payload = {
          idTipoComprobante: comprobanteForm.idTipoComprobante,
          numero: comprobanteForm.numero,
          fechaComprobante: comprobanteForm.fechaComprobante,
          entidadTipo: comprobanteForm.entidadTipo,
          idCliente: comprobanteForm.entidadTipo === 'Cliente' ? comprobanteForm.idCliente : null,
          idProveedor: comprobanteForm.entidadTipo === 'Proveedor' ? comprobanteForm.idProveedor : null,
          importeTotal: parseAmount(comprobanteForm.importeTotal),
          notas: comprobanteForm.notas
        };
        if (comprobanteForm.id) {
          const response = await ctaCteApiService.updateComprobante(comprobanteForm.id, payload);
          savedComprobante = getValue(response, 'comprobante', 'Comprobante');
        } else {
          const response = await ctaCteApiService.createComprobante(payload);
          savedComprobante = getValue(response, 'comprobante', 'Comprobante');
        }
        setComprobanteForm(emptyComprobanteForm());
        setShowComprobanteModal(false);
        await refreshComprobanteResults();
      });
      if (!isEditing && savedComprobante) {
        setComprobanteCtaCtePrompt(savedComprobante);
      } else {
        showSuccess('Comprobante guardado correctamente.');
      }
    } catch (error) {
      showError(error, 'No pudimos guardar el comprobante.');
    }
  };

  const editComprobante = (comprobante) => {
    const entidadTipo = getValue(comprobante, 'entidadTipo', 'EntidadTipo') || 'Cliente';
    setComprobanteForm({
      id: getId(comprobante),
      idTipoComprobante: getValue(comprobante, 'idTipoComprobante', 'IdTipoComprobante') || '',
      numero: getValue(comprobante, 'numero', 'Numero') || '',
      fechaComprobante: String(getValue(comprobante, 'fechaComprobante', 'FechaComprobante') || '').slice(0, 10) || todayInput(),
      entidadTipo,
      idCliente: getValue(comprobante, 'idCliente', 'IdCliente') || '',
      idProveedor: getValue(comprobante, 'idProveedor', 'IdProveedor') || '',
      importeTotal: formatAmountInput(getValue(comprobante, 'importeTotal', 'ImporteTotal') || ''),
      notas: getValue(comprobante, 'notas', 'Notas') || ''
    });
    setShowComprobanteModal(true);
  };

  const deleteComprobante = async (comprobante) => {
    if (!window.confirm(`¿Eliminar el comprobante ${getValue(comprobante, 'numero', 'Numero') || ''}?`)) return;
    try {
      await runWithProcessing('Eliminando comprobante...', async () => {
        await ctaCteApiService.deleteComprobante(getId(comprobante));
        await refreshComprobanteResults();
      });
      showSuccess('Comprobante eliminado.');
    } catch (error) {
      showError(error, 'No pudimos eliminar el comprobante.');
    }
  };

  const cuentaColumns = [
    {
      key: 'nombre',
      header: 'Nombre',
      value: (cuenta) => getNombre(cuenta),
      render: (cuenta) => (
        <span className="ctacte-label-cell">
          <strong>{getNombre(cuenta)}</strong>
        </span>
      )
    },
    {
      key: 'codigo',
      header: 'Código',
      value: (cuenta) => getValue(cuenta, 'codigo', 'Codigo') || 'Sin código'
    },
    {
      key: 'etiqueta',
      header: 'Etiqueta',
      value: (cuenta) => getNombre(getValue(cuenta, 'etiqueta', 'Etiqueta')) || 'Sin etiqueta',
      render: (cuenta) => {
        const etiqueta = getValue(cuenta, 'etiqueta', 'Etiqueta');
        return etiqueta ? (
          <span className="ctacte-label-cell">
            <span className="ctacte-color-dot" style={{ backgroundColor: getValue(etiqueta, 'color', 'Color') }} />
            <strong>{getNombre(etiqueta)}</strong>
          </span>
        ) : '-';
      }
    },
    {
      key: 'subcuentas',
      header: 'Subcuentas',
      value: (cuenta) => {
        const subcuentas = getList(cuenta, 'subcuentas', 'Subcuentas');
        return subcuentas.length;
      },
      exportValue: (cuenta) => getList(cuenta, 'subcuentas', 'Subcuentas').length,
      searchValue: (cuenta) => {
        const subcuentas = getList(cuenta, 'subcuentas', 'Subcuentas');
        return subcuentas.map((subcuenta) => getNombre(subcuenta)).join(' ');
      },
      render: (cuenta) => {
        const subcuentas = getList(cuenta, 'subcuentas', 'Subcuentas');
        return (
          <button type="button" className="ctacte-count-btn" onClick={() => openSubcuentasDetail(cuenta)} title="Ver subcuentas">
            {subcuentas.length}
          </button>
        );
      }
    },
    {
      key: 'descripcion',
      header: 'Descripción',
      value: (cuenta) => getValue(cuenta, 'descripcion', 'Descripcion') || 'Sin descripción'
    },
    {
      key: 'estado',
      header: 'Estado',
      value: (cuenta) => (getValue(cuenta, 'activo', 'Activo') === false ? 'Inactiva' : 'Activa'),
      render: (cuenta) => (
        <span className={`ctacte-pill ${getValue(cuenta, 'activo', 'Activo') === false ? 'inactive' : 'active'}`}>
          {getValue(cuenta, 'activo', 'Activo') === false ? 'Inactiva' : 'Activa'}
        </span>
      )
    }
  ];

  const subcuentaDetailColumns = [
    {
      key: 'nombre',
      header: 'Subcuenta',
      value: (subcuenta) => getNombre(subcuenta),
      render: (subcuenta) => (
        <span className="ctacte-label-cell">
          <strong>{getNombre(subcuenta)}</strong>
        </span>
      )
    },
    {
      key: 'codigo',
      header: 'Código',
      value: (subcuenta) => getValue(subcuenta, 'codigo', 'Codigo') || 'Sin código'
    },
    {
      key: 'descripcion',
      header: 'Descripción',
      value: (subcuenta) => getValue(subcuenta, 'descripcion', 'Descripcion') || 'Sin descripción'
    },
    {
      key: 'estado',
      header: 'Estado',
      value: (subcuenta) => (getValue(subcuenta, 'activo', 'Activo') === false ? 'Inactiva' : 'Activa'),
      render: (subcuenta) => (
        <span className={`ctacte-pill ${getValue(subcuenta, 'activo', 'Activo') === false ? 'inactive' : 'active'}`}>
          {getValue(subcuenta, 'activo', 'Activo') === false ? 'Inactiva' : 'Activa'}
        </span>
      )
    }
  ];

  const buildMovimientoPayload = (form) => {
    const entidadTipo = form.entidadTipo || '';
    return {
      tipoMovimiento: form.tipoMovimiento,
      idCuenta: form.idCuenta || null,
      idSubcuenta: form.idCuenta ? (form.idSubcuenta || null) : null,
      entidadTipo: entidadTipo || null,
      idCliente: entidadTipo === 'Cliente' ? (form.idCliente || null) : null,
      idProveedor: entidadTipo === 'Proveedor' ? (form.idProveedor || null) : null,
      idComprobante: form.idComprobante || null,
      concepto: form.concepto,
      notas: form.notas,
      importe: parseAmount(form.importe),
      fechaMovimiento: form.fechaMovimiento
    };
  };

  const persistMovimientoForm = async (form, options = {}) => {
    const message = form.id ? 'Guardando movimiento...' : 'Creando movimiento...';
    try {
      await runWithProcessing(message, async () => {
        const payload = buildMovimientoPayload(form);
        if (form.id) {
          await ctaCteApiService.updateMovimiento(form.id, payload);
        } else {
          await ctaCteApiService.createMovimiento(payload);
        }
        await refreshMovimientoResults();
      });
      options.onSuccess?.();
      showSuccess(options.successMessage || 'Movimiento guardado correctamente.');
      return true;
    } catch (error) {
      showError(error, options.errorMessage || 'No pudimos guardar el movimiento.');
      return false;
    }
  };

  const saveMovimiento = async (event) => {
    event.preventDefault();
    await persistMovimientoForm(movimientoForm, {
      onSuccess: () => setMovimientoForm(emptyMovimientoForm())
    });
  };

  const saveMovimientoComprobante = async (event) => {
    event.preventDefault();
    await persistMovimientoForm(movimientoForm, {
      successMessage: 'Comprobante cargado en cuenta corriente correctamente.',
      errorMessage: 'No pudimos cargar el comprobante en cuenta corriente.',
      onSuccess: () => {
        setShowMovimientoComprobanteModal(false);
        setMovimientoForm(emptyMovimientoForm());
      }
    });
  };

  const editMovimiento = (movimiento) => {
    setMovimientoForm({
      id: getId(movimiento),
      tipoMovimiento: getValue(movimiento, 'tipoMovimiento', 'TipoMovimiento') || 'Debito',
      idCuenta: getValue(movimiento, 'idCuenta', 'IdCuenta') || '',
      idSubcuenta: getValue(movimiento, 'idSubcuenta', 'IdSubcuenta') || '',
      entidadTipo: getValue(movimiento, 'entidadTipo', 'EntidadTipo') || '',
      idCliente: getValue(movimiento, 'idCliente', 'IdCliente') || '',
      idProveedor: getValue(movimiento, 'idProveedor', 'IdProveedor') || '',
      idComprobante: getValue(movimiento, 'idComprobante', 'IdComprobante') || '',
      concepto: getValue(movimiento, 'concepto', 'Concepto') || '',
      importe: formatAmountInput(getValue(movimiento, 'importe', 'Importe') || ''),
      fechaMovimiento: String(getValue(movimiento, 'fechaMovimiento', 'FechaMovimiento') || '').slice(0, 10) || todayInput(),
      notas: getValue(movimiento, 'notas', 'Notas') || ''
    });
    setActiveMenu('ctacte');
  };

  const deleteMovimiento = async (movimiento) => {
    if (!window.confirm('¿Eliminar este movimiento?')) return;
    try {
      await runWithProcessing('Eliminando movimiento...', async () => {
        await ctaCteApiService.deleteMovimiento(getId(movimiento));
        await refreshMovimientoResults();
      });
      showSuccess('Movimiento eliminado.');
    } catch (error) {
      showError(error, 'No pudimos eliminar el movimiento.');
    }
  };

  const procesoColumns = [
    {
      key: 'fechaAlta',
      header: 'Creado',
      value: (proceso) => formatDateTime(getValue(proceso, 'fechaAlta', 'FechaAlta')),
      exportValue: (proceso) => formatDateTime(getValue(proceso, 'fechaAlta', 'FechaAlta'))
    },
    {
      key: 'nombre',
      header: 'Proceso',
      value: (proceso) => getValue(proceso, 'nombre', 'Nombre') || '-',
      render: (proceso) => (
        <span className="ctacte-stacked-cell compact">
          <strong>{getValue(proceso, 'nombre', 'Nombre') || '-'}</strong>
          <span>{getValue(proceso, 'tipoProceso', 'TipoProceso') || '-'}</span>
        </span>
      )
    },
    {
      key: 'estado',
      header: 'Estado',
      value: (proceso) => getValue(proceso, 'estado', 'Estado') || '-',
      render: (proceso) => {
        const estado = getValue(proceso, 'estado', 'Estado') || 'Pendiente';
        return <span className={`ctacte-pill process-${getProcesoEstadoClass(estado)}`}>{estado}</span>;
      }
    },
    {
      key: 'porcentaje',
      header: 'Avance',
      value: (proceso) => `${Number(getValue(proceso, 'porcentaje', 'Porcentaje') || 0)}%`,
      exportValue: (proceso) => Number(getValue(proceso, 'porcentaje', 'Porcentaje') || 0),
      render: (proceso) => {
        const porcentaje = Math.max(0, Math.min(100, Number(getValue(proceso, 'porcentaje', 'Porcentaje') || 0)));
        return (
          <span className="ctacte-progress-cell">
            <span className="ctacte-progress-track">
              <span style={{ width: `${porcentaje}%` }} />
            </span>
            <strong>{porcentaje}%</strong>
          </span>
        );
      }
    },
    {
      key: 'mensaje',
      header: 'Mensaje',
      value: (proceso) => getValue(proceso, 'error', 'Error') || getValue(proceso, 'mensaje', 'Mensaje') || '-',
      render: (proceso) => {
        const error = getValue(proceso, 'error', 'Error');
        return (
          <span className={`ctacte-process-message ${error ? 'error' : ''}`}>
            {error || getValue(proceso, 'mensaje', 'Mensaje') || '-'}
          </span>
        );
      }
    },
    {
      key: 'fechaFin',
      header: 'Finalizado',
      value: (proceso) => formatDateTime(getValue(proceso, 'fechaFin', 'FechaFin')),
      exportValue: (proceso) => formatDateTime(getValue(proceso, 'fechaFin', 'FechaFin'))
    }
  ];

  const renderProcesoActions = (proceso) => {
    const estado = getValue(proceso, 'estado', 'Estado');
    const canDownload = estado === 'Completado';
    return (
      <>
        <button
          type="button"
          className="action-excel"
          onClick={() => downloadProceso(proceso, 'excel')}
          disabled={!canDownload || !getValue(proceso, 'tieneExcel', 'TieneExcel')}
          title="Descargar Excel"
        >
          <i className="fas fa-file-excel" />
        </button>
        <button
          type="button"
          className="action-pdf"
          onClick={() => downloadProceso(proceso, 'pdf')}
          disabled={!canDownload || !getValue(proceso, 'tienePdf', 'TienePdf')}
          title="Descargar PDF"
        >
          <i className="fas fa-file-pdf" />
        </button>
      </>
    );
  };

  const renderReportChart = (title, items = []) => {
    const visibleItems = Array.isArray(items) ? items.slice(0, 8) : [];
    const maxValue = Math.max(1, ...visibleItems.map((item) => Math.abs(Number(getValue(item, 'valor', 'Valor') || 0))));
    return (
      <article className="ctacte-chart-card">
        <h3>{title}</h3>
        <div className="ctacte-chart-bars">
          {visibleItems.map((item) => {
            const value = Number(getValue(item, 'valor', 'Valor') || 0);
            const width = Math.max(5, Math.round((Math.abs(value) / maxValue) * 100));
            return (
              <div className="ctacte-chart-row" key={`${title}-${getValue(item, 'label', 'Label')}`}>
                <span>{getValue(item, 'label', 'Label') || '-'}</span>
                <div className="ctacte-chart-track">
                  <span style={{ width: `${width}%` }} />
                </div>
                <strong>{formatCurrency(value)}</strong>
              </div>
            );
          })}
          {visibleItems.length === 0 && <p className="ctacte-chart-empty">Sin datos para graficar</p>}
        </div>
      </article>
    );
  };

  const renderResumen = () => {
    const ultimos = getList(resumen, 'ultimosMovimientos', 'UltimosMovimientos');
    return (
      <section className="ctacte-section">
        <div className="ctacte-section-header">
          <div>
            <h2>Resumen</h2>
            <p>Estado general de la cuenta corriente.</p>
          </div>
          <button
            type="button"
            className="ctacte-secondary-btn"
            onClick={() => runWithProcessing('Actualizando resumen...', () => loadBaseData({ showPageLoading: false }))}
            disabled={saving}
          >
            <i className="fas fa-sync-alt" />
            Actualizar
          </button>
        </div>
        <div className="ctacte-kpi-stack">
          <div className="ctacte-kpi-grid summary-main">
            <article className="ctacte-kpi-income"><span>Ingresos</span><strong>{formatCurrency(getValue(resumen, 'ingresos', 'Ingresos'))}</strong></article>
            <article className="ctacte-kpi-outcome"><span>Salidas</span><strong>{formatCurrency(getValue(resumen, 'salidas', 'Salidas'))}</strong></article>
            <article className="ctacte-kpi-balance"><span>Saldo</span><strong>{formatCurrency(getValue(resumen, 'saldo', 'Saldo'))}</strong></article>
            <article className="ctacte-kpi-month"><span>Saldo del mes</span><strong>{formatCurrency(getValue(resumen, 'saldoMes', 'SaldoMes'))}</strong></article>
          </div>
          <div className="ctacte-kpi-grid summary-secondary">
            <article className="ctacte-kpi-accounts"><span>Cuentas</span><strong>{getValue(resumen, 'cuentas', 'Cuentas') || 0}</strong></article>
            <article className="ctacte-kpi-subaccounts"><span>Subcuentas</span><strong>{getValue(resumen, 'subcuentas', 'Subcuentas') || 0}</strong></article>
          </div>
        </div>
        <div className="ctacte-panel">
          <h3>Últimos movimientos</h3>
          {renderMovimientosTable(ultimos, {
            title: 'Últimos movimientos',
            exportFileName: 'ultimos-movimientos',
            defaultPageSize: 8
          })}
        </div>
      </section>
    );
  };

  const renderEtiquetas = () => (
    <section className="ctacte-section">
      <div className="ctacte-section-header">
        <div>
          <h2>Etiquetas</h2>
          <p>ABM de etiquetas para clasificar cuentas.</p>
        </div>
      </div>
      <form className="ctacte-form-grid" onSubmit={saveEtiqueta}>
        <label>Nombre<input value={etiquetaForm.nombre} onChange={(e) => setEtiquetaForm((prev) => ({ ...prev, nombre: e.target.value }))} required /></label>
        <label>Color<input type="color" value={etiquetaForm.color} onChange={(e) => setEtiquetaForm((prev) => ({ ...prev, color: e.target.value }))} /></label>
        <label className="wide">Descripción<input value={etiquetaForm.descripcion} onChange={(e) => setEtiquetaForm((prev) => ({ ...prev, descripcion: e.target.value }))} /></label>
        <div className="ctacte-form-actions">
          <button type="button" className="ctacte-secondary-btn" onClick={() => setEtiquetaForm(emptyEtiquetaForm)}>Limpiar</button>
          <button type="submit" className="ctacte-primary-btn" disabled={saving}>{etiquetaForm.id ? 'Guardar' : 'Crear'}</button>
        </div>
      </form>
      <DataGrid
        title="Etiquetas creadas"
        rows={etiquetasGridRows}
        columns={etiquetaColumns}
        rowKey={getId}
        emptyText="Sin etiquetas"
        searchPlaceholder="Buscar etiquetas"
        exportFileName="etiquetas"
        actions={(etiqueta) => {
          const isActive = getValue(etiqueta, 'activo', 'Activo') !== false;
          return (
            <>
              <button type="button" className="action-edit" onClick={() => editEtiqueta(etiqueta)} title="Editar">
                <i className="fas fa-pen" />
              </button>
              {isActive && (
                <button type="button" className="action-disable" onClick={() => deactivateEtiqueta(etiqueta)} title="Desactivar">
                  <i className="fas fa-ban" />
                </button>
              )}
              <button type="button" className="action-delete danger" onClick={() => deleteEtiqueta(etiqueta)} title="Eliminar permanentemente">
                <i className="fas fa-trash" />
              </button>
            </>
          );
        }}
      />
    </section>
  );

  const renderCuentas = () => (
    <section className="ctacte-section">
      <div className="ctacte-section-header">
        <div>
          <h2>Cuentas</h2>
          <p>ABM de cuentas y subcuentas.</p>
        </div>
        <button type="button" className="ctacte-primary-btn" onClick={openCreateCuentaModal}>
          <i className="fas fa-plus" />
          Nueva cuenta
        </button>
      </div>

      <DataGrid
        title="Cuentas creadas"
        rows={cuentas}
        columns={cuentaColumns}
        rowKey={getId}
        emptyText="Sin cuentas"
        searchPlaceholder="Buscar cuentas"
        exportFileName="cuentas"
        actions={(cuenta) => (
          <>
            <button type="button" className="action-add" onClick={() => startSubcuenta(cuenta)} title="Agregar subcuenta">
              <i className="fas fa-plus" />
            </button>
            <button type="button" className="action-edit" onClick={() => editCuenta(cuenta)} title="Editar">
              <i className="fas fa-pen" />
            </button>
            <button type="button" className="action-delete danger" onClick={() => deleteCuenta(cuenta)} title="Eliminar">
              <i className="fas fa-trash" />
            </button>
          </>
        )}
      />

      {selectedSubcuentasCuenta && (
        <div className="modal-overlay" onClick={closeSubcuentasDetail}>
          <div className="modal-content ctacte-detail-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-layer-group" />
                Subcuentas de {getNombre(selectedSubcuentasCuenta)}
              </h3>
              <button type="button" className="modal-close-btn" onClick={closeSubcuentasDetail}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <DataGrid
                title={`Subcuentas de ${getNombre(selectedSubcuentasCuenta)}`}
                rows={selectedSubcuentasRows}
                columns={subcuentaDetailColumns}
                rowKey={getId}
                emptyText="Sin subcuentas"
                searchPlaceholder="Buscar subcuentas"
                exportFileName={`subcuentas-${getValue(selectedSubcuentasCuenta, 'codigo', 'Codigo') || getNombre(selectedSubcuentasCuenta)}`}
                actions={(subcuenta) => (
                  <>
                    <button type="button" className="action-edit" onClick={() => startSubcuenta(selectedSubcuentasCuenta, subcuenta)} title="Editar">
                      <i className="fas fa-pen" />
                    </button>
                    <button type="button" className="action-delete danger" onClick={() => deleteSubcuenta(selectedSubcuentasCuenta, subcuenta)} title="Eliminar">
                      <i className="fas fa-trash" />
                    </button>
                  </>
                )}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeSubcuentasDetail}>Cerrar</button>
              <button type="button" className="btn btn-primary" onClick={() => startSubcuenta(selectedSubcuentasCuenta)}>
                <i className="fas fa-plus" />
                Nueva subcuenta
              </button>
            </div>
          </div>
        </div>
      )}

      {showCuentaModal && (
        <div className="modal-overlay" onClick={closeCuentaModal}>
          <form className="modal-content ctacte-form-modal" onSubmit={saveCuenta} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-wallet" />
                {cuentaForm.id ? 'Editar cuenta' : 'Nueva cuenta'}
              </h3>
              <button type="button" className="modal-close-btn" onClick={closeCuentaModal} disabled={saving}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <div className="ctacte-modal-form">
                <label>Nombre
                  <input value={cuentaForm.nombre} onChange={(e) => setCuentaForm((prev) => ({ ...prev, nombre: e.target.value }))} required />
                </label>
                <label>Código
                  <input
                    value={cuentaForm.codigo}
                    onChange={(e) => setCuentaForm((prev) => ({ ...prev, codigo: e.target.value }))}
                    placeholder={codeSuggestionLoading === 'cuenta' ? 'Calculando...' : 'Código'}
                  />
                </label>
                <label>Etiqueta
                  <SearchableSelect
                    value={cuentaForm.idEtiqueta}
                    onChange={(value) => setCuentaForm((prev) => ({ ...prev, idEtiqueta: value }))}
                    options={etiquetaOptions}
                    placeholder="Buscar etiqueta"
                    emptyText="No hay etiquetas cargadas."
                  />
                </label>
                <label className="wide">Descripción
                  <input value={cuentaForm.descripcion} onChange={(e) => setCuentaForm((prev) => ({ ...prev, descripcion: e.target.value }))} />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeCuentaModal} disabled={saving}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                <i className="fas fa-save" />
                {cuentaForm.id ? 'Guardar' : 'Crear cuenta'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showSubcuentaModal && (
        <div className="modal-overlay" onClick={closeSubcuentaModal}>
          <form className="modal-content ctacte-form-modal" onSubmit={saveSubcuenta} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-layer-group" />
                {subcuentaForm.id ? 'Editar subcuenta' : 'Nueva subcuenta'}
              </h3>
              <button type="button" className="modal-close-btn" onClick={closeSubcuentaModal} disabled={saving}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <div className="ctacte-modal-form">
                <label>Cuenta
                  <input value={getNombre(cuentas.find((cuenta) => getId(cuenta) === subcuentaForm.idCuenta)) || 'Cuenta seleccionada'} disabled />
                </label>
                <label>Subcuenta
                  <input value={subcuentaForm.nombre} onChange={(e) => setSubcuentaForm((prev) => ({ ...prev, nombre: e.target.value }))} required />
                </label>
                <label>Código
                  <input
                    value={subcuentaForm.codigo}
                    onChange={(e) => setSubcuentaForm((prev) => ({ ...prev, codigo: e.target.value }))}
                    placeholder={codeSuggestionLoading === 'subcuenta' ? 'Calculando...' : 'Código'}
                  />
                </label>
                <label className="wide">Descripción
                  <input value={subcuentaForm.descripcion} onChange={(e) => setSubcuentaForm((prev) => ({ ...prev, descripcion: e.target.value }))} />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeSubcuentaModal} disabled={saving}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                <i className="fas fa-save" />
                {subcuentaForm.id ? 'Guardar' : 'Crear subcuenta'}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );

  const renderEntidadComercialModal = ({ show, title, icon, form, setForm, onSubmit, onClose }) => (
    show ? (
      <div className="modal-overlay" onClick={onClose}>
        <form className="modal-content ctacte-form-modal" onSubmit={onSubmit} onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <i className={icon} />
              {form.id ? `Editar ${title.toLowerCase()}` : `Nuevo ${title.toLowerCase()}`}
            </h3>
            <button type="button" className="modal-close-btn" onClick={onClose} disabled={saving}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="modal-body">
            <div className="ctacte-modal-form">
              <label>Nombre
                <input value={form.nombre} onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))} required />
              </label>
              <label>CUIT/DNI
                <input value={form.documento} onChange={(e) => setForm((prev) => ({ ...prev, documento: e.target.value }))} />
              </label>
              <label>Email
                <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
              </label>
              <label>Teléfono
                <input value={form.telefono} onChange={(e) => setForm((prev) => ({ ...prev, telefono: e.target.value }))} />
              </label>
              <label className="wide">Dirección
                <input value={form.direccion} onChange={(e) => setForm((prev) => ({ ...prev, direccion: e.target.value }))} />
              </label>
              <label className="wide">Notas
                <textarea value={form.notas} onChange={(e) => setForm((prev) => ({ ...prev, notas: e.target.value }))} />
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <i className="fas fa-save" />
              {form.id ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    ) : null
  );

  const renderEntidadComercial = ({
    title,
    description,
    icon,
    rows,
    form,
    setForm,
    showModal,
    openCreate,
    closeModal,
    save,
    edit,
    remove,
    exportFileName,
    singularTitle
  }) => (
    <section className="ctacte-section">
      <div className="ctacte-section-header">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <button type="button" className="ctacte-primary-btn" onClick={openCreate}>
          <i className="fas fa-plus" />
          Nuevo {singularTitle.toLowerCase()}
        </button>
      </div>

      <DataGrid
        title={`${title} creados`}
        rows={rows}
        columns={entidadComercialColumns}
        rowKey={getId}
        emptyText={`Sin ${title.toLowerCase()}`}
        searchPlaceholder={`Buscar ${title.toLowerCase()}`}
        exportFileName={exportFileName}
        actions={(entidad) => (
          <>
            <button type="button" className="action-edit" onClick={() => edit(entidad)} title="Editar">
              <i className="fas fa-pen" />
            </button>
            <button type="button" className="action-delete danger" onClick={() => remove(entidad)} title="Desactivar">
              <i className="fas fa-ban" />
            </button>
          </>
        )}
      />

      {renderEntidadComercialModal({
        show: showModal,
        title: singularTitle,
        icon,
        form,
        setForm,
        onSubmit: save,
        onClose: closeModal
      })}
    </section>
  );

  const renderClientes = () => renderEntidadComercial({
    title: 'Clientes',
    singularTitle: 'Cliente',
    description: 'ABM de clientes para asociar comprobantes.',
    icon: 'fas fa-users',
    rows: clientes,
    form: clienteForm,
    setForm: setClienteForm,
    showModal: showClienteModal,
    openCreate: openCreateClienteModal,
    closeModal: closeClienteModal,
    save: saveCliente,
    edit: editCliente,
    remove: deleteCliente,
    exportFileName: 'clientes'
  });

  const renderProveedores = () => renderEntidadComercial({
    title: 'Proveedores',
    singularTitle: 'Proveedor',
    description: 'ABM de proveedores para asociar comprobantes.',
    icon: 'fas fa-truck',
    rows: proveedores,
    form: proveedorForm,
    setForm: setProveedorForm,
    showModal: showProveedorModal,
    openCreate: openCreateProveedorModal,
    closeModal: closeProveedorModal,
    save: saveProveedor,
    edit: editProveedor,
    remove: deleteProveedor,
    exportFileName: 'proveedores'
  });

  const renderTipoComprobanteModal = () => (
    showTipoComprobanteModal ? (
      <div className="modal-overlay" onClick={closeTipoComprobanteModal}>
        <form className="modal-content ctacte-form-modal" onSubmit={saveTipoComprobante} onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <i className="fas fa-file-alt" />
              {tipoComprobanteForm.id ? 'Editar tipo de comprobante' : 'Nuevo tipo de comprobante'}
            </h3>
            <button type="button" className="modal-close-btn" onClick={closeTipoComprobanteModal} disabled={saving}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="modal-body">
            <div className="ctacte-modal-form">
              <label>Nombre
                <input value={tipoComprobanteForm.nombre} onChange={(e) => setTipoComprobanteForm((prev) => ({ ...prev, nombre: e.target.value }))} required />
              </label>
              <label>Código
                <input value={tipoComprobanteForm.codigo} onChange={(e) => setTipoComprobanteForm((prev) => ({ ...prev, codigo: e.target.value }))} />
              </label>
              <label className="wide">Descripción
                <input value={tipoComprobanteForm.descripcion} onChange={(e) => setTipoComprobanteForm((prev) => ({ ...prev, descripcion: e.target.value }))} />
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeTipoComprobanteModal} disabled={saving}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <i className="fas fa-save" />
              {tipoComprobanteForm.id ? 'Guardar' : 'Crear tipo'}
            </button>
          </div>
        </form>
      </div>
    ) : null
  );

  const renderTiposComprobanteManagerModal = () => (
    showTiposComprobanteManagerModal ? (
      <div className="modal-overlay" onClick={closeTiposComprobanteManagerModal}>
        <div className="modal-content ctacte-detail-modal" onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <i className="fas fa-file-alt" />
              Tipos de comprobante
            </h3>
            <button type="button" className="modal-close-btn" onClick={closeTiposComprobanteManagerModal} disabled={saving}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="modal-body">
            <div className="ctacte-modal-toolbar">
              <button type="button" className="ctacte-secondary-btn" onClick={openCreateTipoComprobanteModal} disabled={saving}>
                <i className="fas fa-plus" />
                Nuevo tipo
              </button>
            </div>
            <DataGrid
              title="Tipos de comprobante"
              rows={tiposComprobante}
              columns={tipoComprobanteColumns}
              rowKey={getId}
              emptyText="Sin tipos de comprobante"
              searchPlaceholder="Buscar tipos"
              exportFileName="tipos-comprobante"
              defaultPageSize={5}
              actions={(tipo) => (
                <>
                  <button type="button" className="action-edit" onClick={() => editTipoComprobante(tipo)} title="Editar">
                    <i className="fas fa-pen" />
                  </button>
                  {getValue(tipo, 'activo', 'Activo') !== false && (
                    <button type="button" className="action-disable" onClick={() => deleteTipoComprobante(tipo)} title="Desactivar">
                      <i className="fas fa-ban" />
                    </button>
                  )}
                </>
              )}
            />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeTiposComprobanteManagerModal} disabled={saving}>Cerrar</button>
          </div>
        </div>
      </div>
    ) : null
  );

  const renderMovimientoFormFields = () => (
    <>
      <label>Cuenta
        <SearchableSelect
          value={movimientoForm.idCuenta}
          onChange={(value) => setMovimientoForm((prev) => ({ ...prev, idCuenta: value, idSubcuenta: '' }))}
          options={[{ value: '', label: 'Sin cuenta' }, ...cuentaOptions]}
          placeholder="Sin cuenta"
          emptyText="No hay cuentas cargadas."
        />
      </label>
      {movimientoSubcuentas.length > 0 && (
        <label>Subcuenta
          <SearchableSelect
            value={movimientoForm.idSubcuenta}
            onChange={(value) => setMovimientoForm((prev) => ({ ...prev, idSubcuenta: value }))}
            options={movimientoSubcuentaOptions}
            placeholder="Sin subcuenta"
          />
        </label>
      )}
      <label>Relación
        <SearchableSelect
          value={movimientoForm.entidadTipo}
          onChange={(value) => setMovimientoForm((prev) => ({
            ...prev,
            entidadTipo: value,
            idCliente: '',
            idProveedor: '',
            idComprobante: ''
          }))}
          options={movimientoEntidadOptions}
          placeholder="Sin cliente/proveedor"
        />
      </label>
      {movimientoForm.entidadTipo === 'Cliente' && (
        <label>Cliente
          <SearchableSelect
            value={movimientoForm.idCliente}
            onChange={(value) => setMovimientoForm((prev) => ({ ...prev, idCliente: value, idComprobante: '' }))}
            options={clienteOptions}
            placeholder="Seleccionar cliente"
            emptyText="No hay clientes cargados."
          />
        </label>
      )}
      {movimientoForm.entidadTipo === 'Proveedor' && (
        <label>Proveedor
          <SearchableSelect
            value={movimientoForm.idProveedor}
            onChange={(value) => setMovimientoForm((prev) => ({ ...prev, idProveedor: value, idComprobante: '' }))}
            options={proveedorOptions}
            placeholder="Seleccionar proveedor"
            emptyText="No hay proveedores cargados."
          />
        </label>
      )}
      <label>Comprobante
        <SearchableSelect
          value={movimientoForm.idComprobante}
          onChange={applyComprobanteToMovimientoForm}
          options={movimientoComprobanteOptions}
          placeholder="Sin comprobante"
          emptyText="No hay comprobantes cargados."
        />
      </label>
      <label>Tipo
        <SearchableSelect
          value={movimientoForm.tipoMovimiento}
          onChange={(value) => setMovimientoForm((prev) => ({ ...prev, tipoMovimiento: value || 'Debito' }))}
          options={tipoMovimientoOptions}
          placeholder="Tipo"
        />
      </label>
      <label>Fecha<input type="date" value={movimientoForm.fechaMovimiento} onChange={(e) => setMovimientoForm((prev) => ({ ...prev, fechaMovimiento: e.target.value }))} /></label>
      <label>Importe
        <input
          value={movimientoForm.importe}
          onChange={(e) => {
            const inputType = e.nativeEvent?.inputType || '';
            const data = e.nativeEvent?.data || '';
            const inputValue = e.target.value;
            setMovimientoForm((prev) => ({
              ...prev,
              importe: formatAmountAsTyping(inputValue, prev.importe, inputType, data)
            }));
          }}
          onBlur={() => setMovimientoForm((prev) => ({ ...prev, importe: formatAmountInput(prev.importe) }))}
          onFocus={(e) => e.target.select()}
          placeholder="$0,00"
          inputMode="decimal"
        />
      </label>
      <label className="wide">Concepto<input value={movimientoForm.concepto} onChange={(e) => setMovimientoForm((prev) => ({ ...prev, concepto: e.target.value }))} /></label>
      <label className="wide">Notas<textarea value={movimientoForm.notas} onChange={(e) => setMovimientoForm((prev) => ({ ...prev, notas: e.target.value }))} /></label>
    </>
  );

  const renderComprobanteCtaCtePrompt = () => (
    comprobanteCtaCtePrompt ? (
      <div className="modal-overlay" onClick={closeComprobanteCtaCtePrompt}>
        <div className="modal-content ctacte-confirm-modal" onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <i className="fas fa-file-invoice-dollar" />
              Cargar en cuenta corriente
            </h3>
            <button type="button" className="modal-close-btn" onClick={closeComprobanteCtaCtePrompt} disabled={saving}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="modal-body">
            <p className="ctacte-confirm-text">
              El comprobante {getValue(comprobanteCtaCtePrompt, 'numero', 'Numero') || ''} se guardó correctamente.
            </p>
            <p className="ctacte-confirm-text">
              ¿Querés cargarlo también como movimiento de cuenta corriente?
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeComprobanteCtaCtePrompt} disabled={saving}>No</button>
            <button type="button" className="btn btn-primary" onClick={openMovimientoFromComprobante} disabled={saving}>
              <i className="fas fa-check" />
              Sí, cargar movimiento
            </button>
          </div>
        </div>
      </div>
    ) : null
  );

  const renderMovimientoComprobanteModal = () => (
    showMovimientoComprobanteModal ? (
      <div className="modal-overlay" onClick={closeMovimientoComprobanteModal}>
        <form className="modal-content ctacte-form-modal" onSubmit={saveMovimientoComprobante} onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <i className="fas fa-file-invoice-dollar" />
              Cargar comprobante en Cta Cte
            </h3>
            <button type="button" className="modal-close-btn" onClick={closeMovimientoComprobanteModal} disabled={saving}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="modal-body">
            <div className="ctacte-modal-form">
              {renderMovimientoFormFields()}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeMovimientoComprobanteModal} disabled={saving}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <i className="fas fa-save" />
              Confirmar movimiento
            </button>
          </div>
        </form>
      </div>
    ) : null
  );

  const renderComprobanteModal = () => (
    showComprobanteModal ? (
      <div className="modal-overlay" onClick={closeComprobanteModal}>
        <form className="modal-content ctacte-form-modal" onSubmit={saveComprobante} onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">
              <i className="fas fa-file-invoice" />
              {comprobanteForm.id ? 'Editar comprobante' : 'Cargar comprobante'}
            </h3>
            <button type="button" className="modal-close-btn" onClick={closeComprobanteModal} disabled={saving}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="modal-body">
            <div className="ctacte-modal-form">
              <label>Tipo
                <SearchableSelect
                  value={comprobanteForm.idTipoComprobante}
                  onChange={(value) => setComprobanteForm((prev) => ({ ...prev, idTipoComprobante: value }))}
                  options={tipoComprobanteOptions}
                  placeholder="Seleccionar tipo"
                  emptyText="No hay tipos de comprobante."
                />
              </label>
              <label>Número
                <input value={comprobanteForm.numero} onChange={(e) => setComprobanteForm((prev) => ({ ...prev, numero: e.target.value }))} required />
              </label>
              <label>Fecha
                <input type="date" value={comprobanteForm.fechaComprobante} onChange={(e) => setComprobanteForm((prev) => ({ ...prev, fechaComprobante: e.target.value }))} />
              </label>
              <label>Entidad
                <SearchableSelect
                  value={comprobanteForm.entidadTipo}
                  onChange={(value) => setComprobanteForm((prev) => ({ ...prev, entidadTipo: value || 'Cliente', idCliente: '', idProveedor: '' }))}
                  options={entidadComprobanteOptions}
                  placeholder="Cliente o proveedor"
                />
              </label>
              {comprobanteForm.entidadTipo === 'Proveedor' ? (
                <label>Proveedor
                  <SearchableSelect
                    value={comprobanteForm.idProveedor}
                    onChange={(value) => setComprobanteForm((prev) => ({ ...prev, idProveedor: value }))}
                    options={proveedorOptions}
                    placeholder="Seleccionar proveedor"
                    emptyText="No hay proveedores cargados."
                  />
                </label>
              ) : (
                <label>Cliente
                  <SearchableSelect
                    value={comprobanteForm.idCliente}
                    onChange={(value) => setComprobanteForm((prev) => ({ ...prev, idCliente: value }))}
                    options={clienteOptions}
                    placeholder="Seleccionar cliente"
                    emptyText="No hay clientes cargados."
                  />
                </label>
              )}
              <label>Importe total
                <input
                  value={comprobanteForm.importeTotal}
                  onChange={(e) => {
                    const inputType = e.nativeEvent?.inputType || '';
                    const data = e.nativeEvent?.data || '';
                    const inputValue = e.target.value;
                    setComprobanteForm((prev) => ({
                      ...prev,
                      importeTotal: formatAmountAsTyping(inputValue, prev.importeTotal, inputType, data)
                    }));
                  }}
                  onBlur={() => setComprobanteForm((prev) => ({ ...prev, importeTotal: formatAmountInput(prev.importeTotal) }))}
                  onFocus={(e) => e.target.select()}
                  placeholder="$0,00"
                  inputMode="decimal"
                  required
                />
              </label>
              <label className="wide">Notas
                <textarea value={comprobanteForm.notas} onChange={(e) => setComprobanteForm((prev) => ({ ...prev, notas: e.target.value }))} />
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeComprobanteModal} disabled={saving}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <i className="fas fa-save" />
              {comprobanteForm.id ? 'Guardar' : 'Cargar comprobante'}
            </button>
          </div>
        </form>
      </div>
    ) : null
  );

  const renderComprobantes = () => (
    <section className="ctacte-section">
      <div className="ctacte-section-header">
        <div>
          <h2>Comprobantes</h2>
          <p>Tipos de comprobantes y comprobantes de clientes o proveedores.</p>
        </div>
        <div className="ctacte-header-actions">
          <button type="button" className="ctacte-secondary-btn" onClick={openTiposComprobanteManagerModal}>
            <i className="fas fa-file-alt" />
            Tipos de comprobante
          </button>
          <button type="button" className="ctacte-primary-btn" onClick={openCreateComprobanteModal}>
            <i className="fas fa-plus" />
            Cargar comprobante
          </button>
        </div>
      </div>

      <div className="ctacte-filter-bar comprobantes">
        <input placeholder="Buscar" value={comprobanteFilters.search} onChange={(e) => setComprobanteFilters((prev) => ({ ...prev, search: e.target.value }))} />
        <SearchableSelect
          value={comprobanteFilters.idTipoComprobante}
          onChange={(value) => setComprobanteFilters((prev) => ({ ...prev, idTipoComprobante: value }))}
          options={tipoComprobanteFilterOptions}
          placeholder="Todos los comprobantes"
        />
        <SearchableSelect
          value={comprobanteFilters.entidadTipo}
          onChange={(value) => setComprobanteFilters((prev) => ({ ...prev, entidadTipo: value, idCliente: '', idProveedor: '' }))}
          options={entidadComprobanteFilterOptions}
          placeholder="Cliente o proveedor"
        />
        {comprobanteFilters.entidadTipo !== 'Proveedor' && (
          <SearchableSelect
            value={comprobanteFilters.idCliente}
            onChange={(value) => setComprobanteFilters((prev) => ({ ...prev, idCliente: value }))}
            options={[{ value: '', label: 'Todos los clientes' }, ...clienteOptions.filter((option) => option.value)]}
            placeholder="Todos los clientes"
          />
        )}
        {comprobanteFilters.entidadTipo !== 'Cliente' && (
          <SearchableSelect
            value={comprobanteFilters.idProveedor}
            onChange={(value) => setComprobanteFilters((prev) => ({ ...prev, idProveedor: value }))}
            options={[{ value: '', label: 'Todos los proveedores' }, ...proveedorOptions.filter((option) => option.value)]}
            placeholder="Todos los proveedores"
          />
        )}
        <input type="date" value={comprobanteFilters.fechaDesde} onChange={(e) => setComprobanteFilters((prev) => ({ ...prev, fechaDesde: e.target.value }))} />
        <input type="date" value={comprobanteFilters.fechaHasta} onChange={(e) => setComprobanteFilters((prev) => ({ ...prev, fechaHasta: e.target.value }))} />
        <button
          type="button"
          className="ctacte-secondary-btn"
          onClick={() => runWithProcessing('Filtrando comprobantes...', () => loadComprobantes())}
          disabled={saving}
        >
          Filtrar
        </button>
      </div>

      <div className="ctacte-kpi-grid compact">
        <article><span>Importe total</span><strong>{formatCurrency(comprobantesResumen.importeTotal)}</strong></article>
        <article><span>Comprobantes</span><strong>{comprobantesTotal}</strong></article>
        <article><span>Clientes</span><strong>{clientes.filter((cliente) => getValue(cliente, 'activo', 'Activo') !== false).length}</strong></article>
        <article><span>Proveedores</span><strong>{proveedores.filter((proveedor) => getValue(proveedor, 'activo', 'Activo') !== false).length}</strong></article>
      </div>

      <DataGrid
        title="Comprobantes cargados"
        rows={comprobantes}
        columns={comprobanteColumns}
        rowKey={getId}
        emptyText="Sin comprobantes"
        searchPlaceholder="Buscar comprobantes"
        exportFileName="comprobantes"
        actions={(comprobante) => (
          <>
            <button type="button" className="action-edit" onClick={() => editComprobante(comprobante)} title="Editar">
              <i className="fas fa-pen" />
            </button>
            <button type="button" className="action-delete danger" onClick={() => deleteComprobante(comprobante)} title="Eliminar">
              <i className="fas fa-trash" />
            </button>
          </>
        )}
      />

      {renderTiposComprobanteManagerModal()}
      {renderTipoComprobanteModal()}
      {renderComprobanteModal()}
      {renderComprobanteCtaCtePrompt()}
      {renderMovimientoComprobanteModal()}
    </section>
  );

  const renderMovimientosTable = (rows = movimientos, options = {}) => (
    <DataGrid
      title={options.title || 'Movimientos'}
      rows={rows}
      columns={movimientoColumns}
      rowKey={getId}
      emptyText="Sin movimientos"
      searchPlaceholder="Buscar en la grilla"
      exportFileName={options.exportFileName || 'movimientos'}
      defaultPageSize={options.defaultPageSize || 10}
      actions={(movimiento) => (
        <>
          <button type="button" className="action-edit" onClick={() => editMovimiento(movimiento)} title="Editar">
            <i className="fas fa-pen" />
          </button>
          <button type="button" className="action-delete danger" onClick={() => deleteMovimiento(movimiento)} title="Eliminar">
            <i className="fas fa-trash" />
          </button>
        </>
      )}
    />
  );

  const renderCtaCte = () => (
    <section className="ctacte-section">
      <div className="ctacte-section-header">
        <div>
          <h2>Cta Cte</h2>
          <p>Movimientos por cuenta y subcuenta.</p>
        </div>
      </div>
      <form className="ctacte-form-grid" onSubmit={saveMovimiento}>
        {renderMovimientoFormFields()}
        <div className="ctacte-form-actions">
          <button type="button" className="ctacte-secondary-btn" onClick={() => setMovimientoForm(emptyMovimientoForm())}>Limpiar</button>
          <button type="submit" className="ctacte-primary-btn" disabled={saving}>{movimientoForm.id ? 'Guardar' : 'Crear movimiento'}</button>
        </div>
      </form>

      <div className="ctacte-filter-bar movimientos">
        <input placeholder="Buscar" value={movimientoFilters.search} onChange={(e) => setMovimientoFilters((prev) => ({ ...prev, search: e.target.value }))} />
        <SearchableSelect
          value={movimientoFilters.tipoMovimiento}
          onChange={(value) => setMovimientoFilters((prev) => ({ ...prev, tipoMovimiento: value }))}
          options={tipoMovimientoFilterOptions}
          placeholder="Todos los tipos"
        />
        <SearchableSelect
          value={movimientoFilters.idCuenta}
          onChange={(value) => setMovimientoFilters((prev) => ({ ...prev, idCuenta: value, idSubcuenta: '' }))}
          options={cuentaFilterOptions}
          placeholder="Todas las cuentas"
          emptyText="No hay cuentas cargadas."
        />
        {filterSubcuentas.length > 0 && (
          <SearchableSelect
            value={movimientoFilters.idSubcuenta}
            onChange={(value) => setMovimientoFilters((prev) => ({ ...prev, idSubcuenta: value }))}
            options={filterSubcuentaOptions}
            placeholder="Todas las subcuentas"
          />
        )}
        <SearchableSelect
          value={movimientoFilters.entidadTipo}
          onChange={(value) => setMovimientoFilters((prev) => ({
            ...prev,
            entidadTipo: value,
            idCliente: '',
            idProveedor: '',
            idComprobante: ''
          }))}
          options={movimientoEntidadFilterOptions}
          placeholder="Todas las entidades"
        />
        {movimientoFilters.entidadTipo !== 'Proveedor' && (
          <SearchableSelect
            value={movimientoFilters.idCliente}
            onChange={(value) => setMovimientoFilters((prev) => ({ ...prev, idCliente: value, idComprobante: '' }))}
            options={[{ value: '', label: 'Todos los clientes' }, ...clienteOptions.filter((option) => option.value)]}
            placeholder="Todos los clientes"
          />
        )}
        {movimientoFilters.entidadTipo !== 'Cliente' && (
          <SearchableSelect
            value={movimientoFilters.idProveedor}
            onChange={(value) => setMovimientoFilters((prev) => ({ ...prev, idProveedor: value, idComprobante: '' }))}
            options={[{ value: '', label: 'Todos los proveedores' }, ...proveedorOptions.filter((option) => option.value)]}
            placeholder="Todos los proveedores"
          />
        )}
        <SearchableSelect
          value={movimientoFilters.idComprobante}
          onChange={(value) => setMovimientoFilters((prev) => ({ ...prev, idComprobante: value }))}
          options={comprobanteFilterOptions}
          placeholder="Todos los comprobantes"
        />
        <input type="date" value={movimientoFilters.fechaDesde} onChange={(e) => setMovimientoFilters((prev) => ({ ...prev, fechaDesde: e.target.value }))} />
        <input type="date" value={movimientoFilters.fechaHasta} onChange={(e) => setMovimientoFilters((prev) => ({ ...prev, fechaHasta: e.target.value }))} />
        <button
          type="button"
          className="ctacte-secondary-btn"
          onClick={() => runWithProcessing('Filtrando movimientos...', () => loadMovimientos())}
          disabled={saving}
        >
          Filtrar
        </button>
      </div>

      <div className="ctacte-kpi-grid compact">
        <article><span>Ingresos filtrados</span><strong>{formatCurrency(movimientosResumen.ingresos)}</strong></article>
        <article><span>Salidas filtradas</span><strong>{formatCurrency(movimientosResumen.salidas)}</strong></article>
        <article><span>Saldo filtrado</span><strong>{formatCurrency(movimientosResumen.saldo)}</strong></article>
        <article><span>Movimientos</span><strong>{movimientosTotal}</strong></article>
      </div>
      {renderMovimientosTable(movimientos, {
        title: 'Movimientos de cuenta corriente',
        exportFileName: 'movimientos-cuenta-corriente',
        defaultPageSize: 10
      })}
    </section>
  );

  const renderReportes = () => {
    const resumenReporte = getValue(ultimoReporteResultado, 'resumen', 'Resumen') || {};
    const graficosReporte = getValue(ultimoReporteResultado, 'graficos', 'Graficos') || {};

    return (
      <section className="ctacte-section">
        <div className="ctacte-section-header">
          <div>
            <h2>Reportes</h2>
            <p>Generación asincrónica con historial, avance y descargas.</p>
          </div>
          <button
            type="button"
            className="ctacte-secondary-btn"
            onClick={() => runWithProcessing('Actualizando procesos...', () => loadProcesos())}
            disabled={saving}
          >
            <i className="fas fa-sync-alt" />
            Actualizar
          </button>
        </div>

        <form className="ctacte-form-grid reportes" onSubmit={createReporte}>
          <label className="wide">Nombre del reporte
            <input
              value={reporteForm.nombre}
              onChange={(e) => setReporteForm((prev) => ({ ...prev, nombre: e.target.value }))}
              placeholder="Reporte de comprobantes y cuenta corriente"
            />
          </label>
          <label>Desde
            <input type="date" value={reporteForm.fechaDesde} onChange={(e) => setReporteForm((prev) => ({ ...prev, fechaDesde: e.target.value }))} />
          </label>
          <label>Hasta
            <input type="date" value={reporteForm.fechaHasta} onChange={(e) => setReporteForm((prev) => ({ ...prev, fechaHasta: e.target.value }))} />
          </label>
          <label>Tipo comprobante
            <SearchableSelect
              value={reporteForm.idTipoComprobante}
              onChange={(value) => setReporteForm((prev) => ({ ...prev, idTipoComprobante: value }))}
              options={tipoComprobanteFilterOptions}
              placeholder="Todos los comprobantes"
            />
          </label>
          <label>Entidad
            <SearchableSelect
              value={reporteForm.entidadTipo}
              onChange={(value) => setReporteForm((prev) => ({
                ...prev,
                entidadTipo: value,
                idCliente: '',
                idProveedor: ''
              }))}
              options={entidadComprobanteFilterOptions}
              placeholder="Cliente o proveedor"
            />
          </label>
          {reporteForm.entidadTipo === 'Cliente' && (
            <label>Cliente
              <SearchableSelect
                value={reporteForm.idCliente}
                onChange={(value) => setReporteForm((prev) => ({ ...prev, idCliente: value }))}
                options={[{ value: '', label: 'Todos los clientes' }, ...clienteOptions.filter((option) => option.value)]}
                placeholder="Todos los clientes"
              />
            </label>
          )}
          {reporteForm.entidadTipo === 'Proveedor' && (
            <label>Proveedor
              <SearchableSelect
                value={reporteForm.idProveedor}
                onChange={(value) => setReporteForm((prev) => ({ ...prev, idProveedor: value }))}
                options={[{ value: '', label: 'Todos los proveedores' }, ...proveedorOptions.filter((option) => option.value)]}
                placeholder="Todos los proveedores"
              />
            </label>
          )}
          <label>Tipo movimiento
            <SearchableSelect
              value={reporteForm.tipoMovimiento}
              onChange={(value) => setReporteForm((prev) => ({ ...prev, tipoMovimiento: value }))}
              options={tipoMovimientoFilterOptions}
              placeholder="Todos los tipos"
            />
          </label>
          <div className="ctacte-report-switches">
            <label className="ctacte-checkbox">
              <input
                type="checkbox"
                checked={reporteForm.incluirComprobantes}
                onChange={(e) => setReporteForm((prev) => ({ ...prev, incluirComprobantes: e.target.checked }))}
              />
              <span>Comprobantes</span>
            </label>
            <label className="ctacte-checkbox">
              <input
                type="checkbox"
                checked={reporteForm.incluirMovimientos}
                onChange={(e) => setReporteForm((prev) => ({ ...prev, incluirMovimientos: e.target.checked }))}
              />
              <span>Movimientos</span>
            </label>
          </div>
          <div className="ctacte-form-actions">
            <button type="button" className="ctacte-secondary-btn" onClick={resetReporteForm} disabled={saving}>Limpiar</button>
            <button type="submit" className="ctacte-primary-btn" disabled={saving}>
              <i className="fas fa-play" />
              Generar reporte
            </button>
          </div>
        </form>

        <div className="ctacte-kpi-grid compact">
          <article><span>Comprobantes</span><strong>{getValue(resumenReporte, 'totalComprobantes', 'TotalComprobantes') || 0}</strong></article>
          <article><span>Importe comprobantes</span><strong>{formatCurrency(getValue(resumenReporte, 'importeComprobantes', 'ImporteComprobantes'))}</strong></article>
          <article><span>Saldo movimientos</span><strong>{formatCurrency(getValue(resumenReporte, 'saldo', 'Saldo'))}</strong></article>
          <article><span>Procesos activos</span><strong>{procesos.filter((proceso) => ['Pendiente', 'EnProceso'].includes(getValue(proceso, 'estado', 'Estado'))).length}</strong></article>
        </div>

        <div className="ctacte-chart-grid">
          {renderReportChart('Comprobantes por tipo', getValue(graficosReporte, 'comprobantesPorTipo', 'ComprobantesPorTipo') || [])}
          {renderReportChart('Movimientos por tipo', getValue(graficosReporte, 'movimientosPorTipo', 'MovimientosPorTipo') || [])}
          {renderReportChart('Movimientos por mes', getValue(graficosReporte, 'movimientosPorMes', 'MovimientosPorMes') || [])}
        </div>

        <DataGrid
          title="Reportes generados"
          rows={reportesProcesos}
          columns={procesoColumns}
          rowKey={getId}
          emptyText="Sin reportes generados"
          searchPlaceholder="Buscar reportes"
          exportFileName="procesos-reportes"
          actions={renderProcesoActions}
        />
      </section>
    );
  };

  const renderProcesos = () => {
    const procesosPendientes = procesos.filter((proceso) => getValue(proceso, 'estado', 'Estado') === 'Pendiente').length;
    const procesosActivos = procesos.filter((proceso) => getValue(proceso, 'estado', 'Estado') === 'EnProceso').length;
    const procesosCompletados = procesos.filter((proceso) => getValue(proceso, 'estado', 'Estado') === 'Completado').length;
    const procesosError = procesos.filter((proceso) => getValue(proceso, 'estado', 'Estado') === 'Error').length;

    return (
      <section className="ctacte-section">
        <div className="ctacte-section-header">
          <div>
            <h2>Procesos</h2>
            <p>Seguimiento de trabajos asincrónicos, progreso y errores.</p>
          </div>
          <button
            type="button"
            className="ctacte-secondary-btn"
            onClick={() => runWithProcessing('Actualizando procesos...', () => loadProcesos())}
            disabled={saving}
          >
            <i className="fas fa-sync-alt" />
            Actualizar
          </button>
        </div>

        <div className="ctacte-filter-bar procesos">
          <input placeholder="Buscar proceso" value={procesosFilters.search} onChange={(e) => setProcesosFilters((prev) => ({ ...prev, search: e.target.value }))} />
          <SearchableSelect
            value={procesosFilters.estado}
            onChange={(value) => setProcesosFilters((prev) => ({ ...prev, estado: value }))}
            options={[
              { value: '', label: 'Todos los estados' },
              { value: 'Pendiente', label: 'Pendiente' },
              { value: 'EnProceso', label: 'En proceso' },
              { value: 'Completado', label: 'Completado' },
              { value: 'Error', label: 'Error' }
            ]}
            placeholder="Todos los estados"
          />
          <SearchableSelect
            value={procesosFilters.tipoProceso}
            onChange={(value) => setProcesosFilters((prev) => ({ ...prev, tipoProceso: value }))}
            options={[
              { value: '', label: 'Todos los procesos' },
              { value: 'ReporteCtaCte', label: 'Reportes Cta Cte' }
            ]}
            placeholder="Todos los procesos"
          />
          <button
            type="button"
            className="ctacte-secondary-btn"
            onClick={() => runWithProcessing('Filtrando procesos...', () => loadProcesos())}
            disabled={saving}
          >
            Filtrar
          </button>
        </div>

        <div className="ctacte-kpi-grid compact">
          <article><span>Total</span><strong>{procesosTotal}</strong></article>
          <article><span>Pendientes</span><strong>{procesosPendientes}</strong></article>
          <article><span>En proceso</span><strong>{procesosActivos}</strong></article>
          <article><span>Completados</span><strong>{procesosCompletados}</strong></article>
          <article><span>Con error</span><strong>{procesosError}</strong></article>
        </div>

        <DataGrid
          title="Procesos"
          rows={procesos}
          columns={procesoColumns}
          rowKey={getId}
          emptyText="Sin procesos"
          searchPlaceholder="Buscar en procesos"
          exportFileName="procesos"
          actions={renderProcesoActions}
        />
      </section>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="ctacte-empty page">
          <LoadingSpinner message="Cargando datos..." inline />
        </div>
      );
    }
    if (activeMenu === 'etiquetas') return renderEtiquetas();
    if (activeMenu === 'cuentas') return renderCuentas();
    if (activeMenu === 'ctacte') return renderCtaCte();
    if (activeMenu === 'comprobantes') return renderComprobantes();
    if (activeMenu === 'reportes') return renderReportes();
    if (activeMenu === 'procesos') return renderProcesos();
    if (activeMenu === 'clientes') return renderClientes();
    if (activeMenu === 'proveedores') return renderProveedores();
    return renderResumen();
  };

  return (
    <div className={`ctacte-app ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {sidebarOpen && <div className="ctacte-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`ctacte-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="ctacte-brand">
          <img src={ctaCteLogo} alt="CtaCte App" className="ctacte-brand-logo" />
          <span>CtaCte App</span>
        </div>
        <nav>
          {menuItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={activeMenu === item.id ? 'active' : ''}
              onClick={() => {
                setActiveMenu(item.id);
                setSidebarOpen(false);
              }}
            >
              <i className={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
      <main className="ctacte-main">
        <header className="ctacte-header">
          <div>
            <button type="button" className="ctacte-menu-btn" onClick={() => setSidebarOpen((prev) => !prev)}>
              <i className="fas fa-bars" />
            </button>
            <div>
              <h1>{menuItems.find((item) => item.id === activeMenu)?.label || 'Resumen'}</h1>
              <p>{user?.displayName || user?.name || user?.email || 'Usuario'}</p>
            </div>
          </div>
          <UserSessionMenu
            user={user}
            onLogout={logout}
            userFallbackLabel="Usuario"
            showStandaloneLogout={false}
            extraItems={[
              {
                key: 'suscripcion',
                label: 'Suscripción',
                icon: 'fas fa-crown',
                onClick: () => setShowSuscripcionModal(true)
              }
            ]}
          />
        </header>
        {toast.text && (
          <div className={`ctacte-toast ${toast.type}`} role="status" aria-live="polite">
            <span className="ctacte-toast-icon">
              <i className={toast.type === 'success' ? 'fas fa-check' : 'fas fa-exclamation-triangle'} />
            </span>
            <div className="ctacte-toast-copy">
              <strong>{toast.type === 'success' ? 'Listo' : 'Atención'}</strong>
              <p>{toast.text}</p>
            </div>
            <button type="button" onClick={closeToast} aria-label="Cerrar mensaje">
              <i className="fas fa-times" />
            </button>
          </div>
        )}
        {renderContent()}
        {processingMessage && <LoadingSpinner message={processingMessage} size="small" />}
      </main>
      {showSuscripcionModal && (
        <SuscripcionModal onClose={() => setShowSuscripcionModal(false)} />
      )}
    </div>
  );
};

export default CtaCteDashboard;
